import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import { optimizeImageFile } from "./imageOptimize";
import Nav from "./Nav";
import { EventDiscordLinks } from "./EventDiscordLinks";

export const ACCENT = "#fcd657";
export const GOLD = "#fcd657";
const EVENT_TYPES = ["Combat", "Industry", "Salvage", "Training", "Piracy", "Exploration", "Other"];
export const TYPE_COLORS = {
  Combat: "#ef5350", Industry: "#fcd657", Salvage: "#a78bfa",
  Training: "#4fc3f7", Piracy: "#f87171", Exploration: "#34d399", Other: "#ce93d8",
};
const PRESET_COLORS = ["#ef5350","#fcd657","#facc15","#4fc3f7","#81c784","#34d399","#a78bfa","#ce93d8","#f87171","#00fffb","#ff7043","#ffffff"];

const VIDEO_EXTENSIONS = ["mp4", "webm", "mov"];
const ACCEPTED_MEDIA_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "video/mp4", "video/webm", "video/quicktime"];
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB for videos/GIFs
function isVideoUrl(url) {
  if (!url) return false;
  const lower = url.toLowerCase().split("?")[0];
  return VIDEO_EXTENSIONS.some(ext => lower.endsWith(`.${ext}`));
}
function isGifUrl(url) {
  if (!url) return false;
  return url.toLowerCase().split("?")[0].endsWith(".gif");
}

function toArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return [val];
}

// Normalise attendees — handles both legacy [userId] and new [{user_id,...}] format
// Also deduplicates by user_id (last entry wins if duplicates exist)
export function normalizeAttendees(raw) {
  if (!raw || !Array.isArray(raw)) return [];
  const byId = {};
  raw.forEach(a => {
    if (typeof a === "string") {
      byId[a] = { user_id: a, username: "", avatar: "", role: "", group: "", rsvp_group: "", sort_order: null };
    } else {
      const uid = a.user_id || "";
      byId[uid] = {
        user_id: uid,
        username: a.username || "",
        avatar: a.avatar || "",
        role: a.role || "",
        group: a.group || "",
        rsvp_group: a.rsvp_group || "",
        sort_order: a.sort_order ?? null,
      };
    }
  });
  return Object.values(byId);
}

export function isUserAttending(attendees, userId) {
  return normalizeAttendees(attendees).some(a => a.user_id === userId);
}

// Permission helper
export function getEventPerms(profile) {
  const orgSort = profile?.org_role_sort;
  const isSuperAdmin = profile?.perm_super_admin === true || profile?.admin_tier === "super_admin";
  // Org leadership = sort values 1.0 through 2.9 (Command + Division Leads)
  const isOrgLeadership = orgSort != null && orgSort >= 1.0 && orgSort < 3.0;
  const isOrgAdmin = isSuperAdmin || isOrgLeadership;

  const userTeams = toArray(profile?.teams);
  const teamRoles = profile?.team_roles || {};
  const ledTeams = userTeams.filter(t => {
    const role = (teamRoles[t] || profile?.team_role || "").toLowerCase();
    return role.includes("team leader") || role.includes("co leader") || role.includes("co-leader");
  });

  return { isOrgAdmin, isOrgLeadership, userTeams, ledTeams };
}

// Determines if a user can join/RSVP to a specific event
export function canJoinEvent(profile, event, perms) {
  if (!profile) return false;
  // Everyone who's logged in can join org-wide events
  if (event.is_org_wide) return true;
  // For team events: org leadership (sort 1.0–2.9) can always join
  if (perms.isOrgLeadership || perms.isOrgAdmin) return true;
  // Team members can join their own team's events
  if (event.team && perms.userTeams.includes(event.team)) return true;
  // Contract events are open to anyone logged in
  if (event.is_contract) return true;
  return false;
}

// ─── Contract Event helpers ────────────────────────────────────────────────────
// Display label for the event's chosen payout currency
export function getCompensationCurrency(event) {
  const comp = event?.compensation || {};
  if (!comp.currency) return "";
  if (comp.currency === "Custom") return (comp.currency_custom || "").trim() || "Custom";
  return comp.currency;
}

// Has the event concluded? Auto-flip when the event date has passed, OR
// the creator manually marked it complete.
export function isEventCompleted(event) {
  if (!event) return false;
  if (event.is_completed) return true;
  if (event.date) {
    const end = new Date(`${event.date}T${event.time || "23:59"}`);
    if (!isNaN(end) && end < new Date()) return true;
  }
  return false;
}

// Calculate what each attendee is owed for a contract event. Layers stack:
//   flat_rate  +  group_rate[attendee.group]  +  position_rate[attendee.role]
//                +  rsvp_role_rate[attendee.rsvp_group]  +  bonuses where recipient
// Returns { rows: [{user_id, username, avatar, total_owed, breakdown:{...}}], grand_total }
export function calculateEventPayouts(event) {
  if (!event) return { rows: [], grand_total: 0 };
  const comp = event.compensation || {};
  const attendees = normalizeAttendees(event.attendees);
  const rsvpOpts = Array.isArray(event.rsvp_options) ? event.rsvp_options : [];
  // Only people whose RSVP group is an attending one (or no rsvp_group at all)
  const paidAttendees = attendees.filter(a => {
    if (!a.rsvp_group) return true;
    const opt = rsvpOpts.find(o => o.name === a.rsvp_group);
    return !opt || opt.attending !== false;
  });

  const flat = Number(comp.flat_rate) || 0;
  const groupRates = comp.group_rates || {};
  const positionRates = Array.isArray(comp.position_rates) ? comp.position_rates : [];
  const rsvpRoleRates = comp.rsvp_role_rates || {};
  const bonuses = Array.isArray(comp.bonuses) ? comp.bonuses : [];

  const rows = paidAttendees.map(a => {
    const breakdown = {};
    let total = 0;
    if (flat) { breakdown.flat = flat; total += flat; }
    if (a.group && groupRates[a.group]) {
      const v = Number(groupRates[a.group]) || 0;
      if (v) { breakdown.group = { name: a.group, rate: v }; total += v; }
    }
    if (a.role) {
      const match = positionRates.find(p => (p.name || "").toLowerCase() === a.role.toLowerCase());
      if (match) {
        const v = Number(match.rate) || 0;
        if (v) { breakdown.position = { name: match.name, rate: v }; total += v; }
      }
    }
    if (a.rsvp_group && rsvpRoleRates[a.rsvp_group]) {
      const v = Number(rsvpRoleRates[a.rsvp_group]) || 0;
      if (v) { breakdown.rsvp = { name: a.rsvp_group, rate: v }; total += v; }
    }
    const userBonuses = bonuses
      .filter(b => Array.isArray(b.recipients) && b.recipients.includes(a.user_id))
      .map(b => ({ name: b.name, rate: Number(b.rate) || 0 }))
      .filter(b => b.rate);
    if (userBonuses.length) {
      breakdown.bonuses = userBonuses;
      total += userBonuses.reduce((s, b) => s + b.rate, 0);
    }
    return {
      user_id: a.user_id,
      username: a.username || "",
      avatar: a.avatar || "",
      role: a.role || "",
      group: a.group || "",
      rsvp_group: a.rsvp_group || "",
      total_owed: total,
      breakdown,
    };
  });

  const grand_total = rows.reduce((s, r) => s + r.total_owed, 0);
  return { rows, grand_total };
}

// Per-attendee min / max payout "offer" — for use in event-details view before
// the leader has closed out the event. `min` is the guaranteed pay based on the
// person's current group / role / RSVP. `max` stacks on every bonus they could
// still pick up (bonuses where they're already a recipient, plus all bonuses
// with empty recipient lists that could theoretically land on them).
export function calculateAttendeeRange(event, attendee) {
  if (!event || !attendee) return { min: 0, max: 0, guaranteed_bonuses: 0, potential_bonuses: 0 };
  const comp = event.compensation || {};
  const rsvpOpts = Array.isArray(event.rsvp_options) ? event.rsvp_options : [];
  // If their RSVP is a non-attending one, they get nothing
  if (attendee.rsvp_group) {
    const opt = rsvpOpts.find(o => o.name === attendee.rsvp_group);
    if (opt && opt.attending === false) return { min: 0, max: 0, guaranteed_bonuses: 0, potential_bonuses: 0 };
  }

  const flat = Number(comp.flat_rate) || 0;
  const groupRates = comp.group_rates || {};
  const positionRates = Array.isArray(comp.position_rates) ? comp.position_rates : [];
  const rsvpRoleRates = comp.rsvp_role_rates || {};
  const bonuses = Array.isArray(comp.bonuses) ? comp.bonuses : [];

  let base = 0;
  if (flat) base += flat;
  if (attendee.group && groupRates[attendee.group]) base += Number(groupRates[attendee.group]) || 0;
  if (attendee.role) {
    const match = positionRates.find(p => (p.name || "").toLowerCase() === attendee.role.toLowerCase());
    if (match) base += Number(match.rate) || 0;
  }
  if (attendee.rsvp_group && rsvpRoleRates[attendee.rsvp_group]) {
    base += Number(rsvpRoleRates[attendee.rsvp_group]) || 0;
  }

  const guaranteedBonuses = bonuses
    .filter(b => Array.isArray(b.recipients) && b.recipients.includes(attendee.user_id))
    .reduce((s, b) => s + (Number(b.rate) || 0), 0);

  // Max potential bonuses = guaranteed + any open-recipient bonus they could still get.
  // An "open" bonus is one with no recipients yet (leader hasn't assigned anyone).
  const potentialBonuses = bonuses
    .filter(b => !Array.isArray(b.recipients) || b.recipients.length === 0)
    .reduce((s, b) => s + (Number(b.rate) || 0), 0);

  return {
    min: base + guaranteedBonuses,
    max: base + guaranteedBonuses + potentialBonuses,
    guaranteed_bonuses: guaranteedBonuses,
    potential_bonuses: potentialBonuses,
  };
}

// Event-level min/max — sums per-attendee min and max across all paid attendees.
// Also returns a breakdown of totals per rate bucket for the summary card.
export function calculateEventRange(event) {
  if (!event) return { min: 0, max: 0, buckets: {} };
  const comp = event.compensation || {};
  const attendees = normalizeAttendees(event.attendees);
  const rsvpOpts = Array.isArray(event.rsvp_options) ? event.rsvp_options : [];
  const paid = attendees.filter(a => {
    if (!a.rsvp_group) return true;
    const opt = rsvpOpts.find(o => o.name === a.rsvp_group);
    return !opt || opt.attending !== false;
  });

  let min = 0, max = 0;
  paid.forEach(a => {
    const r = calculateAttendeeRange(event, a);
    min += r.min;
    max += r.max;
  });

  // Aggregate per-bucket totals (how much each rate category costs across the roster)
  const flat = Number(comp.flat_rate) || 0;
  const groupRates = comp.group_rates || {};
  const positionRates = Array.isArray(comp.position_rates) ? comp.position_rates : [];
  const rsvpRoleRates = comp.rsvp_role_rates || {};
  const bonuses = Array.isArray(comp.bonuses) ? comp.bonuses : [];

  const buckets = {
    flat_total: flat * paid.length,
    group_total: 0,
    position_total: 0,
    rsvp_total: 0,
    assigned_bonus_total: 0,
    open_bonus_total: 0,
    attendee_count: paid.length,
  };
  paid.forEach(a => {
    if (a.group && groupRates[a.group]) buckets.group_total += Number(groupRates[a.group]) || 0;
    if (a.role) {
      const m = positionRates.find(p => (p.name || "").toLowerCase() === a.role.toLowerCase());
      if (m) buckets.position_total += Number(m.rate) || 0;
    }
    if (a.rsvp_group && rsvpRoleRates[a.rsvp_group]) buckets.rsvp_total += Number(rsvpRoleRates[a.rsvp_group]) || 0;
  });
  bonuses.forEach(b => {
    const rate = Number(b.rate) || 0;
    if (Array.isArray(b.recipients) && b.recipients.length > 0) {
      buckets.assigned_bonus_total += rate * b.recipients.length;
    } else {
      buckets.open_bonus_total += rate;
    }
  });

  return { min, max, buckets };
}

function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.2, a: Math.random(),
      speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed; if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.15;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

// ─── Attendee Manager Modal ────────────────────────────────────────────────────
function AttendeeManagerModal({ event, onClose, onSave }) {
  const attendees = normalizeAttendees(event.attendees);
  const [rows, setRows] = useState(attendees);
  const [saving, setSaving] = useState(false);

  function updateRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function removeRow(idx) {
    setRows(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    setSaving(true);
    await onSave(event.id, rows);
    setSaving(false);
    onClose();
  }

  const eventGroups = event.groups || [];

  const inputStyle = {
    padding: "8px 10px", background: "rgba(255,255,255,0.04)",
    border: `1px solid ${ACCENT}33`, color: "#e8eaf6",
    fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none",
    width: "100%", boxSizing: "border-box",
  };

  const labelStyle = {
    fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2,
    color: "rgba(232,234,246,0.4)", textTransform: "uppercase", marginBottom: 4, display: "block",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)",
      backdropFilter: "blur(8px)", display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "env(safe-area-inset-top, 12px) clamp(8px, 2vw, 24px) env(safe-area-inset-bottom, 12px)",
      overflowY: "auto",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "100%", maxWidth: 720, margin: "clamp(12px, 4vh, 40px) 0",
        background: "#0a0610", border: `1px solid ${ACCENT}44`,
        padding: "clamp(16px, 4vw, 36px) clamp(12px, 3vw, 32px)",
        position: "relative",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, width: 24, height: 24, borderTop: `2px solid ${ACCENT}`, borderLeft: `2px solid ${ACCENT}` }} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(13px, 2vw, 17px)", color: "#e8eaf6", lineHeight: 1.3 }}>
            Manage Attendees — {event.title}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 24, cursor: "pointer", padding: 8, minWidth: 36, minHeight: 36 }}>&times;</button>
        </div>

        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 12 }}>
          {rows.length} attendee{rows.length !== 1 ? "s" : ""}
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
            NO ATTENDEES YET
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rows.map((row, idx) => (
              <div key={row.user_id || idx} style={{
                padding: "12px", border: "1px solid rgba(255,255,255,0.06)",
                background: "rgba(255,255,255,0.02)",
              }}>
                {/* Member header row */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                    background: "#0d1120", border: `1px solid ${ACCENT}44`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {row.avatar
                      ? <img src={row.avatar} alt={row.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontFamily: "'Orbitron'", fontSize: 11, color: ACCENT }}>{(row.username || "?").charAt(0)}</span>
                    }
                  </div>
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.username || row.user_id?.substring(0, 8)}
                    </span>
                    {row.rsvp_group && (() => {
                      const opt = (event.rsvp_options || []).find(o => o.name === row.rsvp_group);
                      const isAtt = opt ? opt.attending : true;
                      return (
                        <span style={{
                          fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1,
                          padding: "2px 6px", flexShrink: 0,
                          border: `1px solid ${isAtt ? "rgba(52,211,153,0.4)" : "rgba(255,255,255,0.15)"}`,
                          color: isAtt ? "#34d399" : "rgba(232,234,246,0.45)",
                          background: isAtt ? "rgba(52,211,153,0.08)" : "transparent",
                          textTransform: "uppercase", lineHeight: 1, whiteSpace: "nowrap",
                        }}>
                          {opt?.emoji} {row.rsvp_group}
                        </span>
                      );
                    })()}
                  </div>
                  <button onClick={() => removeRow(idx)} style={{
                    background: "none", border: "1px solid rgba(239,83,80,0.3)",
                    color: "#ef5350", fontSize: 14, cursor: "pointer", padding: "6px 10px",
                    borderRadius: 2, transition: "all 0.2s", lineHeight: 1, flexShrink: 0,
                    minWidth: 36, minHeight: 36, display: "flex", alignItems: "center", justifyContent: "center",
                  }} title="Remove attendee">✕</button>
                </div>

                {/* Fields — responsive grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 8 }}>
                  <div>
                    <span style={labelStyle}>Role</span>
                    <input value={row.role} onChange={e => updateRow(idx, "role", e.target.value)} placeholder="Role..." style={inputStyle} />
                  </div>
                  <div>
                    <span style={labelStyle}>Group</span>
                    <select value={row.group} onChange={e => updateRow(idx, "group", e.target.value)} style={{ ...inputStyle, background: "#0a0610" }}>
                      <option value="">— No Group —</option>
                      {eventGroups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <span style={labelStyle}>Sort</span>
                    <input type="number" value={row.sort_order ?? ""} onChange={e => updateRow(idx, "sort_order", e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="#" style={{ ...inputStyle, textAlign: "center" }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" }}>
          <button onClick={handleSave} disabled={saving} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
            padding: "12px 24px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`,
            color: ACCENT, transition: "all 0.2s", flex: "1 1 auto", minHeight: 44,
            opacity: saving ? 0.4 : 1,
          }}>{saving ? "Saving..." : "Save Changes"}</button>
          <button onClick={onClose} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2,
            textTransform: "uppercase", cursor: "pointer",
            padding: "12px 24px", background: "none", flex: "1 1 auto", minHeight: 44,
            border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)",
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Event Card ────────────────────────────────────────────────────────────────
function StarRatingInline({ value, size = 14, color = GOLD }) {
  return (
    <span style={{ display: "inline-flex", gap: 1, alignItems: "center" }}>
      {[1, 2, 3, 4].map(s => (
        <span key={s} style={{ fontSize: size, color: s <= value ? color : "rgba(255,255,255,0.12)", lineHeight: 1 }}>★</span>
      ))}
    </span>
  );
}

// ─── Share Modal ──────────────────────────────────────────────────────────────
export function ShareModal({ event, onClose }) {
  const [channels, setChannels] = useState([]);
  const [members, setMembers] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [channelSearch, setChannelSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [allowedChannelIds, setAllowedChannelIds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const [chRes, mRes, sRes] = await Promise.all([
          fetch("/api/discord-channels"),
          fetch("/api/discord-members"),
          supabase.from("app_settings").select("key, value").eq("key", "share_channels").single(),
        ]);
        if (!mounted) return;
        if (chRes.ok) { const d = await chRes.json(); setChannels(d.channels || []); }
        if (mRes.ok) { const d = await mRes.json(); setMembers(d.members || []); }
        if (sRes.data?.value) {
          try { setAllowedChannelIds(JSON.parse(sRes.data.value)); } catch { setAllowedChannelIds([]); }
        } else { setAllowedChannelIds([]); }
      } catch (err) { console.warn("Failed to load share data:", err); }
      if (mounted) setLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, []);

  const filteredChannels = channels.filter(ch => {
    if (allowedChannelIds && !allowedChannelIds.includes(ch.id)) return false;
    if (!channelSearch) return true;
    const q = channelSearch.toLowerCase();
    return ch.name.toLowerCase().includes(q) || (ch.category || "").toLowerCase().includes(q);
  });

  const filteredMembers = members.filter(m => {
    if (!memberSearch) return true;
    const q = memberSearch.toLowerCase();
    return (m.discord_username || "").toLowerCase().includes(q) || (m.nickname || "").toLowerCase().includes(q) || (m.discord_handle || "").toLowerCase().includes(q);
  });

  function toggleChannel(id) { setSelectedChannels(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]); }
  function toggleMember(id) { setSelectedMembers(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]); }

  function copyLink() {
    const url = `${window.location.origin}/share/event/${event.id}`;
    navigator.clipboard.writeText(url).then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }).catch(() => {
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  async function sendToDiscord() {
    if (selectedChannels.length === 0 && selectedMembers.length === 0) return;
    setSending(true);
    try {
      const r = await fetch("/api/discord-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "share_event",
          event,
          channel_ids: selectedChannels,
          discord_ids: selectedMembers,
        }),
      });
      const data = await r.json();
      setSendResult(data);
      setSent(true);
    } catch (err) {
      console.error("Share failed:", err);
      setSendResult({ errors: [{ error: err.message }] });
    }
    setSending(false);
  }

  const totalSelected = selectedChannels.length + selectedMembers.length;

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px 16px max(16px, env(safe-area-inset-bottom, 16px))" }}>
      <div style={{ width: "100%", maxWidth: 580, maxHeight: "calc(100vh - 32px)", display: "flex", flexDirection: "column", background: "#0a0610", border: `1px solid ${ACCENT}44`, position: "relative", clipPath: "polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)", overflow: "hidden" }}>
        {/* Corner accent */}
        <div style={{ position: "absolute", top: 0, left: 0, width: 24, height: 24, borderTop: `2px solid ${ACCENT}`, borderLeft: `2px solid ${ACCENT}`, pointerEvents: "none", zIndex: 2 }} />

        {/* Sticky Header */}
        <div style={{ flexShrink: 0, padding: "20px 24px 12px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "#0a0610" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 17, color: "#e8eaf6", margin: 0 }}>Share Event</h3>
            <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 24, lineHeight: 1, cursor: "pointer", padding: 4, marginRight: -4 }}>×</button>
          </div>
        </div>

        {/* Scrollable Middle */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 24px" }}>
          {/* Event title */}
          <div style={{ padding: "10px 14px", marginBottom: 16, background: `${TYPE_COLORS[event.type] || ACCENT}0a`, border: `1px solid ${TYPE_COLORS[event.type] || ACCENT}33` }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 700, color: TYPE_COLORS[event.type] || ACCENT }}>{event.title}</div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", marginTop: 4 }}>{event.type}{event.team ? ` · ${event.team}` : ""}{event.is_org_wide ? " · Org-Wide" : ""}</div>
          </div>

          {/* Copy Link */}
          <div style={{ marginBottom: 20 }}>
            <button onClick={copyLink} style={{
              width: "100%", padding: "12px 16px", fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
              background: linkCopied ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.03)",
              border: linkCopied ? "1px solid rgba(52,211,153,0.5)" : "1px solid rgba(255,255,255,0.12)",
              color: linkCopied ? "#34d399" : "#e8eaf6", cursor: "pointer", transition: "all 0.2s",
            }}>{linkCopied ? "✓ Link Copied to Clipboard" : "Copy Share Link"}</button>
          </div>

          {loading ? (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.2)", letterSpacing: 2, textAlign: "center", padding: 40 }}>LOADING...</div>
          ) : sent ? (
            <div style={{ textAlign: "center", padding: 20 }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 16, fontWeight: 700, color: "#34d399", marginBottom: 12 }}>Sent!</div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", marginBottom: 6 }}>
                {sendResult?.channels?.length ? `${sendResult.channels.length} channel${sendResult.channels.length !== 1 ? "s" : ""}` : ""}
                {sendResult?.channels?.length && sendResult?.dms?.length ? " & " : ""}
                {sendResult?.dms?.length ? `${sendResult.dms.length} DM${sendResult.dms.length !== 1 ? "s" : ""}` : ""}
              </div>
              {sendResult?.errors?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#ef5350" }}>{sendResult.errors.length} failed</div>
                  {sendResult.errors.map((e, i) => (
                    <div key={i} style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(239,83,80,0.7)", marginTop: 4, wordBreak: "break-all" }}>
                      {e.target}: {typeof e.error === "string" ? e.error : JSON.stringify(e.error)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Channels Section */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#34d399", textTransform: "uppercase", marginBottom: 10 }}>Channels</div>
                <input
                  value={channelSearch} onChange={e => setChannelSearch(e.target.value)}
                  placeholder="Search channels..."
                  style={{ width: "100%", padding: "8px 12px", marginBottom: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(52,211,153,0.2)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {filteredChannels.length === 0 ? (
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 2, padding: 8 }}>
                      {allowedChannelIds?.length === 0 ? "NO CHANNELS CONFIGURED — ASK AN ADMIN" : "NO MATCHING CHANNELS"}
                    </div>
                  ) : filteredChannels.map(ch => {
                    const sel = selectedChannels.includes(ch.id);
                    return (
                      <button key={ch.id} onClick={() => toggleChannel(ch.id)} style={{
                        padding: "5px 12px", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, fontWeight: 600,
                        background: sel ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.03)",
                        border: sel ? "1px solid rgba(52,211,153,0.5)" : "1px solid rgba(255,255,255,0.08)",
                        color: sel ? "#34d399" : "rgba(232,234,246,0.5)", cursor: "pointer", transition: "all 0.15s",
                      }}>{sel ? "✓ " : ""}#{ch.name}</button>
                    );
                  })}
                </div>
              </div>

              {/* DMs Section */}
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#ce93d8", textTransform: "uppercase", marginBottom: 10 }}>Direct Messages</div>
                <input
                  value={memberSearch} onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search members..."
                  style={{ width: "100%", padding: "8px 12px", marginBottom: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(206,147,216,0.2)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {filteredMembers.length === 0 ? (
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(255,255,255,0.2)", letterSpacing: 2, padding: 8 }}>NO MATCHING MEMBERS</div>
                  ) : filteredMembers.map(m => {
                    const sel = selectedMembers.includes(m.discord_id);
                    return (
                      <button key={m.discord_id} onClick={() => toggleMember(m.discord_id)} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", textAlign: "left",
                        background: sel ? "rgba(206,147,216,0.12)" : "rgba(255,255,255,0.02)",
                        border: sel ? "1px solid rgba(206,147,216,0.4)" : "1px solid rgba(255,255,255,0.06)",
                        cursor: "pointer", transition: "all 0.15s",
                      }}>
                        {m.discord_avatar ? (
                          <img src={m.discord_avatar} alt="" style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(206,147,216,0.2)", flexShrink: 0 }} />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, fontWeight: 600, color: sel ? "#ce93d8" : "#e8eaf6", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {m.nickname || m.discord_username}
                          </div>
                          {m.nickname && m.discord_username !== m.nickname && (
                            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)" }}>{m.discord_handle}</div>
                          )}
                        </div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: sel ? "#ce93d8" : "rgba(255,255,255,0.15)", flexShrink: 0 }}>{sel ? "✓" : ""}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sticky Footer (Send / Done) */}
        {!loading && (
          <div style={{ flexShrink: 0, padding: "12px 24px 16px 24px", borderTop: "1px solid rgba(255,255,255,0.08)", background: "#0a0610" }}>
            {sent ? (
              <button onClick={onClose} style={{
                width: "100%", padding: "14px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700,
                background: `${ACCENT}18`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer",
              }}>Done</button>
            ) : (
              <button onClick={sendToDiscord} disabled={sending || totalSelected === 0} style={{
                width: "100%", padding: "14px 20px", fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700,
                background: totalSelected > 0 ? `${ACCENT}18` : "rgba(255,255,255,0.03)",
                border: `1px solid ${totalSelected > 0 ? ACCENT : "rgba(255,255,255,0.1)"}`,
                color: totalSelected > 0 ? ACCENT : "rgba(232,234,246,0.25)",
                cursor: totalSelected > 0 ? "pointer" : "default", transition: "all 0.2s",
                opacity: sending ? 0.6 : 1,
              }}>
                {sending ? "Sending..." : totalSelected > 0 ? `Send to ${totalSelected} Target${totalSelected !== 1 ? "s" : ""}` : "Select Channels or Members"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function EventCard({ event, canEdit, canJoin, onEdit, onArchive, onRSVP, isAttending, profile, onManageAttendees, onShareToOrg, canShareToOrg, reviewData, hasReminder, reminderSchedule, onToggleReminder, isHighlighted, onManagePayouts }) {
  const color = TYPE_COLORS[event.type] || "#ce93d8";
  const evDate = event.date ? new Date(`${event.date}T${event.time || "00:00"}`) : null;
  const isPast = evDate && evDate < new Date();
  // Timezone conversion: prefer event_timezone, fall back to creator_timezone
  const viewerTz = profile?.timezone || null;
  const creatorTz = event.event_timezone || event.creator_timezone || null;
  let convertedDate = evDate;
  let showTzNote = false;
  if (evDate && event.time && creatorTz && viewerTz && creatorTz !== viewerTz) {
    try {
      // Build a date string interpreted in the creator's timezone, then display in viewer's timezone
      const creatorStr = evDate.toLocaleString("en-US", { timeZone: creatorTz });
      const creatorUtc = new Date(creatorStr + " UTC");
      // This is a workaround: compute offset difference
      const cOff = new Date(evDate.toLocaleString("en-US", { timeZone: creatorTz })).getTime();
      const vOff = new Date(evDate.toLocaleString("en-US", { timeZone: viewerTz })).getTime();
      convertedDate = new Date(evDate.getTime() + (vOff - cOff));
      showTzNote = true;
    } catch { convertedDate = evDate; }
  }
  const attendees = normalizeAttendees(event.attendees);
  // Count only attending RSVPs — decliners stay in the list but aren't "signed up"
  const declinedNames = new Set(
    (Array.isArray(event.rsvp_options) ? event.rsvp_options : [])
      .filter(o => o && o.attending === false)
      .map(o => o.name)
  );
  const signedUpCount = attendees.filter(a => !declinedNames.has(a.rsvp_group)).length;
  const [showAttendees, setShowAttendees] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showRsvpPicker, setShowRsvpPicker] = useState(false);
  const [showReminderPicker, setShowReminderPicker] = useState(false);
  const [showRateCard, setShowRateCard] = useState(true);
  const rsvpOptions = Array.isArray(event.rsvp_options) ? event.rsvp_options.filter(o => o.name) : [];
  const myAttendee = profile ? attendees.find(a => a.user_id === profile.id) : null;
  const myRsvpGroup = myAttendee?.rsvp_group || null;

  // Group attendees by group or rsvp_group field
  const grouped = {};
  const ungrouped = [];
  const sorted = [...attendees].sort((a, b) => {
    const sa = a.sort_order ?? 9999;
    const sb = b.sort_order ?? 9999;
    return sa - sb;
  });
  sorted.forEach(a => {
    const grp = a.group;
    if (grp) {
      if (!grouped[grp]) grouped[grp] = [];
      grouped[grp].push(a);
    } else {
      ungrouped.push(a);
    }
  });

  const navigate = useNavigate();
  // Cards are clickable as a whole — but only when the click didn't land on a
  // button, link, input, select, or textarea (so RSVP/edit/share/etc. still work
  // as expected). closest() walks up from the actual click target.
  const handleCardClick = (e) => {
    if (e.target.closest("button, a, input, select, textarea, [data-card-noclick]")) return;
    navigate(`/events/${event.id}/review`);
  };

  return (
    <div id={`event-${event.id}`} onClick={handleCardClick} style={{
      border: isHighlighted ? `1px solid ${color}88` : `1px solid ${color}22`,
      background: isHighlighted ? `${color}14` : `${color}06`,
      padding: "clamp(14px, 3vw, 20px)",
      position: "relative",
      opacity: isPast ? 0.5 : 1,
      transition: "all 0.6s",
      boxShadow: isHighlighted ? `0 0 20px ${color}22` : "none",
      cursor: "pointer",
    }}>
      {/* Color bar */}
      <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: "100%", background: color }} />

      <div style={{ paddingLeft: 12 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: "'Orbitron', sans-serif", fontWeight: 700,
              fontSize: "clamp(13px, 2vw, 16px)", color: "#e8eaf6", marginBottom: 6,
              wordBreak: "break-word",
            }}>{event.title}</div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginBottom: 8 }}>
              <span style={{
                fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1,
                color, textTransform: "uppercase",
              }}>{event.type}</span>
              {evDate && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1,
                  color: "rgba(232,234,246,0.5)",
                }}>
                  {convertedDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                  {event.time && ` · ${convertedDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
                  {showTzNote && <span style={{ color: "rgba(232,234,246,0.3)", marginLeft: 6 }}>(your time)</span>}
                </span>
              )}
              {(event.event_timezone || creatorTz) && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1,
                  color: "rgba(232,234,246,0.25)",
                }}>
                  {(event.event_timezone || creatorTz).replace(/_/g, " ").replace(/^.*\//, "")}
                </span>
              )}
              {event.rally_location && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1,
                  color: "rgba(232,234,246,0.4)",
                }}>📍 {event.rally_location}</span>
              )}
              {event.is_org_wide && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5,
                  color: ACCENT, textTransform: "uppercase", opacity: 0.7,
                }}>ORG-WIDE</span>
              )}
              {event.team && !event.is_org_wide && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5,
                  color: "#ce93d8", textTransform: "uppercase",
                }}>{event.team}</span>
              )}
              {event.is_contract && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5,
                  color: "#34d399", textTransform: "uppercase",
                  padding: "2px 8px", border: "1px solid rgba(52,211,153,0.5)", background: "rgba(52,211,153,0.12)",
                }}>💰 Contract</span>
              )}
            </div>
            {/* Contract summary: grand payout + viewer's slice + expandable rate card */}
            {event.is_contract && (() => {
              const comp = event.compensation || {};
              const { rows: payoutRows, grand_total } = calculateEventPayouts(event);
              const currency = getCompensationCurrency(event);
              const mine = profile ? payoutRows.find(r => r.user_id === profile.id) : null;
              const flat = Number(comp.flat_rate) || 0;
              const groupEntries = Object.entries(comp.group_rates || {}).filter(([, r]) => Number(r) > 0);
              const positionEntries = (comp.position_rates || []).filter(p => p && p.name && Number(p.rate) > 0);
              const rsvpEntries = Object.entries(comp.rsvp_role_rates || {}).filter(([, r]) => Number(r) > 0);
              const bonusEntries = (comp.bonuses || []).filter(b => b && b.name && Number(b.rate) > 0);
              const hasAnyRates = flat > 0 || groupEntries.length > 0 || positionEntries.length > 0 || rsvpEntries.length > 0 || bonusEntries.length > 0;
              if (!hasAnyRates && !grand_total && !mine) return null;
              const fmt = (n) => Number(n).toLocaleString();
              const labelStyle = { fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(52,211,153,0.85)", textTransform: "uppercase", marginBottom: 6 };
              const rowStyle = { display: "flex", flexWrap: "wrap", gap: "2px 10px", marginBottom: 6 };
              const chipStyle = { fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#e8faef", letterSpacing: 0.5, padding: "3px 9px", border: "1px solid rgba(52,211,153,0.4)", background: "rgba(52,211,153,0.1)" };
              return (
                <div style={{
                  marginBottom: 8,
                  padding: "8px 10px", border: "1px solid rgba(52,211,153,0.2)", background: "rgba(52,211,153,0.04)",
                }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", alignItems: "center" }}>
                    {grand_total > 0 && (
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#34d399", letterSpacing: 1 }}>
                        Total payout: <strong>{fmt(grand_total)} {currency}</strong>
                      </span>
                    )}
                    {mine && mine.total_owed > 0 && (
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: ACCENT, letterSpacing: 1 }}>
                        Your cut: <strong>{fmt(mine.total_owed)} {currency}</strong>
                      </span>
                    )}
                    {hasAnyRates && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowRateCard(v => !v); }}
                        style={{
                          marginLeft: "auto",
                          fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2,
                          color: "#34d399", background: "transparent",
                          border: "1px solid rgba(52,211,153,0.4)", padding: "2px 8px",
                          cursor: "pointer", textTransform: "uppercase",
                        }}
                      >
                        {showRateCard ? "▲ Hide Rates" : "▼ View Rates"}
                      </button>
                    )}
                  </div>
                  {showRateCard && hasAnyRates && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed rgba(52,211,153,0.2)" }}>
                      {flat > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={labelStyle}>Flat Rate — every attendee</div>
                          <div style={rowStyle}>
                            <span style={chipStyle}>{fmt(flat)} {currency}</span>
                          </div>
                        </div>
                      )}
                      {groupEntries.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={labelStyle}>Group Bonuses — stacked on flat</div>
                          <div style={rowStyle}>
                            {groupEntries.map(([name, rate]) => {
                              const g = (event.groups || []).find(x => x.name === name);
                              const gColor = g?.color || "#34d399";
                              return (
                                <span key={name} style={{ ...chipStyle, borderColor: `${gColor}55`, color: gColor }}>
                                  {name}: +{fmt(rate)} {currency}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {positionEntries.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={labelStyle}>Position Pay — matched on attendee role</div>
                          <div style={rowStyle}>
                            {positionEntries.map((p, i) => (
                              <span key={i} style={chipStyle}>{p.name}: +{fmt(p.rate)} {currency}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {rsvpEntries.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={labelStyle}>RSVP Role Pay</div>
                          <div style={rowStyle}>
                            {rsvpEntries.map(([name, rate]) => (
                              <span key={name} style={chipStyle}>{name}: +{fmt(rate)} {currency}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {bonusEntries.length > 0 && (
                        <div style={{ marginBottom: 2 }}>
                          <div style={labelStyle}>Bonuses — specific recipients</div>
                          <div style={rowStyle}>
                            {bonusEntries.map((b, i) => (
                              <span key={i} style={chipStyle}>
                                {b.name}: +{fmt(b.rate)} {currency}
                                {Array.isArray(b.recipients) && b.recipients.length > 0 && (
                                  <span style={{ opacity: 0.6, marginLeft: 4 }}>
                                    ({b.recipients.length} {b.recipients.length === 1 ? "person" : "people"})
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Attendance count — clickable to toggle attendee list */}
          <div
            onClick={() => attendees.length > 0 && setShowAttendees(!showAttendees)}
            style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 11,
              color: "rgba(232,234,246,0.4)", textAlign: "right", flexShrink: 0,
              cursor: attendees.length > 0 ? "pointer" : "default",
              transition: "all 0.2s",
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: color + "cc" }}>{signedUpCount}</div>
            <div style={{ fontSize: 8, letterSpacing: 2, textTransform: "uppercase" }}>
              {showAttendees ? "▲ HIDE" : "ATTENDING"}
            </div>
          </div>
        </div>

        {/* Event Image / Video */}
        {event.image_url && (
          <div style={{ marginBottom: 12, maxWidth: 600 }}>
            {isVideoUrl(event.image_url) ? (
              <video src={event.image_url} autoPlay loop muted playsInline style={{
                width: "100%", maxHeight: 260, objectFit: "contain",
                border: `1px solid ${color}22`, background: "rgba(0,0,0,0.3)",
              }} />
            ) : (
              <img src={event.image_url} alt={event.title} style={{
                width: "100%", maxHeight: 260, objectFit: "contain",
                border: `1px solid ${color}22`, background: "rgba(0,0,0,0.3)",
              }} />
            )}
          </div>
        )}

        {/* Command structure — Commander / 2iC / Ops Readiness (briefing doc format) */}
        {(event.commander?.username || event.second_in_command?.username || event.ops_readiness) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, alignItems: "center" }}>
            {event.commander?.username && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1,
                padding: "3px 8px", border: "1px solid rgba(252,214,87,0.35)", color: "#fcd657",
              }}>
                {event.commander.avatar && <img src={event.commander.avatar} alt="" style={{ width: 14, height: 14, borderRadius: "50%" }} />}
                ★ CDR: {event.commander.username}
              </span>
            )}
            {event.second_in_command?.username && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1,
                padding: "3px 8px", border: "1px solid rgba(79,195,247,0.35)", color: "#4fc3f7",
              }}>
                {event.second_in_command.avatar && <img src={event.second_in_command.avatar} alt="" style={{ width: 14, height: 14, borderRadius: "50%" }} />}
                2iC: {event.second_in_command.username}
              </span>
            )}
            {event.ops_readiness && (() => {
              const lvl = { Casual: { emoji: "🟢", color: "#34d399" }, Coordinated: { emoji: "🟡", color: "#fcd657" }, Serious: { emoji: "🔴", color: "#ef5350" } }[event.ops_readiness] || { emoji: "", color: "rgba(232,234,246,0.5)" };
              return (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1,
                  padding: "3px 8px", border: `1px solid ${lvl.color}55`, color: lvl.color, textTransform: "uppercase",
                }}>{lvl.emoji} {event.ops_readiness}</span>
              );
            })()}
          </div>
        )}

        {/* Description — preserve the author's line breaks (white-space: pre-wrap) */}
        {event.description && (
          <p style={{
            fontFamily: "'Rajdhani', sans-serif", fontSize: 14,
            color: "rgba(232,234,246,0.55)", lineHeight: 1.6, marginBottom: 12,
            maxWidth: 600, whiteSpace: "pre-wrap",
          }}>{event.description}</p>
        )}

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
            {event.tags.map(tag => (
              <span key={tag} style={{
                fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1,
                padding: "3px 8px", border: `1px solid ${color}44`, color: color,
                textTransform: "uppercase",
              }}>{tag}</span>
            ))}
          </div>
        )}

        {/* RSVP option groups — show counts per RSVP option */}
        {event.rsvp_options && event.rsvp_options.length > 0 && attendees.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {event.rsvp_options.map((opt, i) => {
              const groupCount = attendees.filter(a => a.rsvp_group === opt.name || (opt.name === "Attending" && !a.rsvp_group)).length;
              return (
                <span key={i} style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1,
                  padding: "3px 8px", border: `1px solid ${opt.attending ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.1)"}`,
                  color: opt.attending ? "#34d399" : "rgba(232,234,246,0.4)",
                  background: opt.attending ? "rgba(52,211,153,0.05)" : "transparent",
                }}>
                  {opt.emoji} {opt.name} ({groupCount})
                </span>
              );
            })}
          </div>
        )}

        {/* Expanded attendee list */}
        {showAttendees && attendees.length > 0 && (
          <div style={{
            marginBottom: 14, padding: "12px 14px",
            border: `1px solid ${color}22`, background: `${color}04`,
          }}>
            {Object.keys(grouped).length > 0 && Object.entries(grouped).map(([groupName, grpMembers]) => {
              const groupDef = (event.groups || []).find(g => g.name === groupName);
              const grpColor = groupDef?.color || color;
              return (
                <div key={groupName} style={{ marginBottom: 10 }}>
                  <div style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 3,
                    color: grpColor, textTransform: "uppercase", marginBottom: 6,
                  }}>{groupName}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {grpMembers.map(a => {
                      const rsvpOpt = (event.rsvp_options || []).find(o => o.name === a.rsvp_group);
                      const rsvpIsAtt = rsvpOpt ? rsvpOpt.attending : true;
                      return (
                        <div key={a.user_id} style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "4px 10px", border: `1px solid ${grpColor}33`, background: `${grpColor}08`,
                          borderRadius: 2,
                        }}>
                          <div style={{
                            width: 22, height: 22, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                            background: "#0d1120", border: `1px solid ${grpColor}44`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {a.avatar
                              ? <img src={a.avatar} alt={a.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : <span style={{ fontFamily: "'Orbitron'", fontSize: 8, color: grpColor }}>{(a.username || "?").charAt(0)}</span>
                            }
                          </div>
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>
                            {a.username || "Unknown"}
                          </span>
                          {a.role && (
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: `${grpColor}aa`, textTransform: "uppercase" }}>
                              {a.role}
                            </span>
                          )}
                          {a.rsvp_group && (
                            <span style={{
                              fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1,
                              padding: "1px 5px",
                              border: `1px solid ${rsvpIsAtt ? "rgba(52,211,153,0.35)" : "rgba(255,255,255,0.12)"}`,
                              color: rsvpIsAtt ? "#34d399" : "rgba(232,234,246,0.4)",
                              background: rsvpIsAtt ? "rgba(52,211,153,0.07)" : "transparent",
                              textTransform: "uppercase", lineHeight: 1, whiteSpace: "nowrap",
                            }}>
                              {rsvpOpt?.emoji} {a.rsvp_group}
                            </span>
                          )}
                          {event.is_contract && (() => {
                            const r = calculateAttendeeRange(event, a);
                            if (!r.min && !r.max) return null;
                            const cur = getCompensationCurrency(event);
                            const label = r.min === r.max
                              ? `${Number(r.min).toLocaleString()} ${cur}`
                              : `${Number(r.min).toLocaleString()}–${Number(r.max).toLocaleString()} ${cur}`;
                            return (
                              <span title={r.min === r.max ? "Guaranteed payout" : "Min – Max payout"} style={{
                                fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1,
                                padding: "1px 5px", color: "#34d399",
                                border: "1px solid rgba(52,211,153,0.35)",
                                background: "rgba(52,211,153,0.07)",
                                lineHeight: 1, whiteSpace: "nowrap",
                              }}>💰 {label}</span>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {ungrouped.length > 0 && (
              <div style={{ marginTop: Object.keys(grouped).length > 0 ? 8 : 0 }}>
                {Object.keys(grouped).length > 0 && (
                  <div style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 3,
                    color: "rgba(232,234,246,0.3)", textTransform: "uppercase", marginBottom: 6,
                  }}>UNASSIGNED</div>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {ungrouped.map(a => {
                    const rsvpOpt = (event.rsvp_options || []).find(o => o.name === a.rsvp_group);
                    const rsvpIsAtt = rsvpOpt ? rsvpOpt.attending : true;
                    return (
                      <div key={a.user_id} style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "4px 10px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)",
                        borderRadius: 2,
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                          background: "#0d1120", border: "1px solid rgba(255,255,255,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {a.avatar
                            ? <img src={a.avatar} alt={a.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <span style={{ fontFamily: "'Orbitron'", fontSize: 8, color: "rgba(232,234,246,0.4)" }}>{(a.username || "?").charAt(0)}</span>
                          }
                        </div>
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>
                          {a.username || "Unknown"}
                        </span>
                        {a.role && (
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
                            {a.role}
                          </span>
                        )}
                        {a.rsvp_group && (
                          <span style={{
                            fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1,
                            padding: "1px 5px",
                            border: `1px solid ${rsvpIsAtt ? "rgba(52,211,153,0.35)" : "rgba(255,255,255,0.12)"}`,
                            color: rsvpIsAtt ? "#34d399" : "rgba(232,234,246,0.4)",
                            background: rsvpIsAtt ? "rgba(52,211,153,0.07)" : "transparent",
                            textTransform: "uppercase", lineHeight: 1, whiteSpace: "nowrap",
                          }}>
                            {rsvpOpt?.emoji} {a.rsvp_group}
                          </span>
                        )}
                        {event.is_contract && (() => {
                          const r = calculateAttendeeRange(event, a);
                          if (!r.min && !r.max) return null;
                          const cur = getCompensationCurrency(event);
                          const label = r.min === r.max
                            ? `${Number(r.min).toLocaleString()} ${cur}`
                            : `${Number(r.min).toLocaleString()}–${Number(r.max).toLocaleString()} ${cur}`;
                          return (
                            <span title={r.min === r.max ? "Guaranteed payout" : "Min – Max payout"} style={{
                              fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1,
                              padding: "1px 5px", color: "#34d399",
                              border: "1px solid rgba(52,211,153,0.35)",
                              background: "rgba(52,211,153,0.07)",
                              lineHeight: 1, whiteSpace: "nowrap",
                            }}>💰 {label}</span>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {/* Contract event: min/max total payout summary */}
          {event.is_contract && (() => {
            const r = calculateEventRange(event);
            if (!r.min && !r.max) return null;
            const cur = getCompensationCurrency(event);
            const fmt = (n) => Number(n).toLocaleString();
            const same = r.min === r.max;
            return (
              <div title={same ? "Guaranteed event payout" : "Min – Max total event payout"} style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "6px 12px", border: "1px solid rgba(52,211,153,0.4)",
                background: "rgba(52,211,153,0.07)",
                fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1,
                color: "#34d399",
              }}>
                <span style={{ fontSize: 13 }}>💰</span>
                <span style={{ fontSize: 8, letterSpacing: 2, color: "rgba(52,211,153,0.7)", textTransform: "uppercase" }}>
                  {same ? "Total" : "Min – Max"}
                </span>
                <strong style={{ color: "#34d399" }}>
                  {same ? `${fmt(r.min)}` : `${fmt(r.min)}–${fmt(r.max)}`} {cur}
                </strong>
              </div>
            );
          })()}
          {/* RSVP section — shows option picker if event has rsvp_options */}
          {(canJoin || isAttending) && !isPast && rsvpOptions.length > 0 && (
            <div style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
              <button
                onClick={() => setShowRsvpPicker(!showRsvpPicker)}
                style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2,
                  textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s",
                  padding: "7px 16px", border: "none", borderRadius: 1,
                  background: isAttending ? `${GOLD}22` : `${color}18`,
                  color: isAttending ? GOLD : color,
                  outline: isAttending ? `1px solid ${GOLD}88` : `1px solid ${color}44`,
                }}
              >
                {isAttending ? `✓ ${myRsvpGroup || "Attending"}` : "RSVP ▾"}
              </button>
              {showRsvpPicker && createPortal(
                <>
                  {/* Click-outside backdrop */}
                  <div onClick={() => setShowRsvpPicker(false)} style={{
                    position: "fixed", inset: 0, zIndex: 1999, background: "transparent",
                  }} />
                  {/* Picker menu */}
                  <div style={{
                    position: "fixed", zIndex: 2000,
                    left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                    background: "#0d0818", border: `1px solid ${color}66`,
                    padding: 0, minWidth: 240, maxWidth: 320,
                    boxShadow: `0 12px 40px rgba(0,0,0,0.8), 0 0 0 1px ${color}22`,
                    backdropFilter: "blur(12px)",
                  }}>
                    <div style={{
                      padding: "12px 16px", borderBottom: `1px solid ${color}22`,
                      fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
                      letterSpacing: 2, color: color, textTransform: "uppercase",
                    }}>RSVP</div>
                    {rsvpOptions.map((opt, i) => {
                      const isSelected = myRsvpGroup === opt.name;
                      return (
                        <button key={i} onClick={() => {
                          onRSVP(event, opt);
                          setShowRsvpPicker(false);
                        }} style={{
                          display: "flex", alignItems: "center", gap: 10, width: "100%",
                          padding: "12px 16px", cursor: "pointer", transition: "all 0.15s",
                          background: isSelected ? `${opt.attending ? "rgba(52,211,153,0.12)" : "rgba(239,83,80,0.12)"}` : "transparent",
                          border: "none", borderBottom: i < rsvpOptions.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                          textAlign: "left",
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                        >
                          <span style={{ fontSize: 20, lineHeight: 1 }}>{opt.emoji}</span>
                          <div>
                            <span style={{
                              fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600,
                              color: isSelected ? (opt.attending ? "#34d399" : "#ef5350") : "#e8eaf6",
                            }}>{opt.name}</span>
                            {opt.attending && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "rgba(52,211,153,0.5)", marginLeft: 8 }}>ATTENDING</span>}
                          </div>
                          {isSelected && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: opt.attending ? "#34d399" : "#ef5350", marginLeft: "auto" }}>✓</span>}
                        </button>
                      );
                    })}
                    {isAttending && (
                      <button onClick={() => { onRSVP(event, null); setShowRsvpPicker(false); }} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "12px 16px", cursor: "pointer", background: "transparent",
                        border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", textAlign: "left",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,83,80,0.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600, color: "rgba(239,83,80,0.7)" }}>Leave Event</span>
                      </button>
                    )}
                  </div>
                </>,
                document.body
              )}
            </div>
          )}
          {/* Fallback simple Join/Leave for events without rsvp_options */}
          {(canJoin || isAttending) && !isPast && rsvpOptions.length === 0 && (
            <button
              onClick={() => onRSVP(event, null)}
              style={{
                fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2,
                textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s",
                padding: "7px 16px", border: "none", borderRadius: 1,
                background: isAttending ? `${GOLD}22` : `${color}18`,
                color: isAttending ? GOLD : color,
                outline: isAttending ? `1px solid ${GOLD}88` : `1px solid ${color}44`,
              }}
            >
              {isAttending ? "✓ Attending" : "Join Event"}
            </button>
          )}
          {/* Reminder signup — only for attending users on upcoming events */}
          {isAttending && !isPast && profile && (
            <div style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
              <button
                onClick={() => setShowReminderPicker(!showReminderPicker)}
                style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
                  textTransform: "uppercase", cursor: "pointer", transition: "all 0.2s",
                  padding: "6px 12px", border: "none", borderRadius: 1,
                  background: hasReminder ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.04)",
                  color: hasReminder ? "#34d399" : "rgba(232,234,246,0.5)",
                  outline: hasReminder ? "1px solid rgba(52,211,153,0.5)" : "1px solid rgba(255,255,255,0.15)",
                }}
              >
                {hasReminder ? "🔔 Reminder Set ▾" : "🔔 Remind Me ▾"}
              </button>
              {showReminderPicker && createPortal(
                <>
                  <div onClick={() => setShowReminderPicker(false)} style={{
                    position: "fixed", inset: 0, zIndex: 1999, background: "transparent",
                  }} />
                  <div style={{
                    position: "fixed", zIndex: 2000,
                    left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                    background: "#0d0818", border: "1px solid rgba(52,211,153,0.4)",
                    padding: 0, minWidth: 260, maxWidth: 320,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.8), 0 0 0 1px rgba(52,211,153,0.1)",
                    backdropFilter: "blur(12px)",
                  }}>
                    <div style={{
                      padding: "12px 16px", borderBottom: "1px solid rgba(52,211,153,0.15)",
                      fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
                      letterSpacing: 2, color: "#34d399", textTransform: "uppercase",
                    }}>Set Reminders</div>
                    {(() => {
                      const timeStr = event.time || "00:00";
                      // Try multiple date formats to handle various stored formats
                      let eventDateTime = new Date(`${event.date}T${timeStr}`);
                      if (isNaN(eventDateTime.getTime())) eventDateTime = new Date(`${event.date}T${timeStr}:00`);
                      if (isNaN(eventDateTime.getTime())) eventDateTime = new Date(`${event.date} ${timeStr}`);
                      if (isNaN(eventDateTime.getTime())) eventDateTime = new Date(event.date);
                      const nowMs = Date.now();
                      const msUntil = isNaN(eventDateTime.getTime()) ? Infinity : eventDateTime.getTime() - nowMs;
                      const activeTypes = (reminderSchedule || []).map(r => r.type);
                      const options = [
                        { type: "at_start", label: "On event start", emoji: "🔔", minMs: 60_000 },
                        { type: "10min",    label: "10 minutes before", emoji: "🔔", minMs: 10 * 60_000 },
                        { type: "1hr",      label: "1 hour before", emoji: "🔔", minMs: 60 * 60_000 },
                        { type: "24hr",     label: "1 day before", emoji: "🔔", minMs: 24 * 60 * 60_000 },
                        { type: "1week",    label: "1 week before", emoji: "🔔", minMs: 7 * 24 * 60 * 60_000 },
                      ];
                      const eligible = options.filter(o => msUntil > o.minMs);
                      if (eligible.length === 0) {
                        return <div style={{ padding: "16px", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(232,234,246,0.5)" }}>Event is starting too soon for reminders.</div>;
                      }
                      return eligible.map((opt, i) => {
                        const isActive = activeTypes.includes(opt.type);
                        return (
                          <button key={opt.type} onClick={() => {
                            onToggleReminder(event, opt.type, isActive);
                          }} style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%",
                            padding: "12px 16px", cursor: "pointer", transition: "all 0.15s",
                            background: isActive ? "rgba(52,211,153,0.12)" : "transparent",
                            border: "none", borderBottom: i < eligible.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                            textAlign: "left",
                          }}
                          onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                          onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = isActive ? "rgba(52,211,153,0.12)" : "transparent"; }}
                          >
                            <span style={{ fontSize: 16, lineHeight: 1 }}>{opt.emoji}</span>
                            <span style={{
                              fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600,
                              color: isActive ? "#34d399" : "#e8eaf6",
                            }}>{opt.label}</span>
                            {isActive && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#34d399", marginLeft: "auto" }}>✓</span>}
                          </button>
                        );
                      });
                    })()}
                    {hasReminder && (
                      <button onClick={() => { onToggleReminder(event, null, true); setShowReminderPicker(false); }} style={{
                        display: "flex", alignItems: "center", gap: 10, width: "100%",
                        padding: "12px 16px", cursor: "pointer", background: "transparent",
                        border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", textAlign: "left",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(239,83,80,0.08)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600, color: "rgba(239,83,80,0.7)" }}>Clear All Reminders</span>
                      </button>
                    )}
                  </div>
                </>,
                document.body
              )}
            </div>
          )}
          {canEdit && (
            <>
              <button onClick={() => onManageAttendees(event)} style={{
                fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
                textTransform: "uppercase", cursor: "pointer",
                padding: "6px 12px", background: "none",
                border: `1px solid #ce93d844`, color: "#ce93d8", transition: "all 0.2s",
              }}>Manage Crew</button>
              {event.is_contract && onManagePayouts && isEventCompleted(event) && (
                <button onClick={() => onManagePayouts(event)} style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
                  textTransform: "uppercase", cursor: "pointer",
                  padding: "6px 12px", background: "rgba(52,211,153,0.12)",
                  border: "1px solid #34d399", color: "#34d399", transition: "all 0.2s",
                }}>💰 Payouts{event.is_completed ? "" : " (Early)"}</button>
              )}
              <button onClick={() => onEdit(event)} style={{
                fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
                textTransform: "uppercase", cursor: "pointer",
                padding: "6px 12px", background: "none",
                border: `1px solid ${ACCENT}33`, color: ACCENT, transition: "all 0.2s",
              }}>Edit</button>
              <button onClick={() => onArchive(event.id)} style={{
                fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
                textTransform: "uppercase", cursor: "pointer",
                padding: "6px 12px", background: "none",
                border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", transition: "all 0.2s",
              }}>Archive</button>
            </>
          )}
          {/* Share button — only visible if event is marked shareable */}
          {profile && event.allow_sharing && (
            <button onClick={() => setShowShareModal(true)} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
              textTransform: "uppercase", cursor: "pointer",
              padding: "6px 12px", background: "none",
              border: "1px solid rgba(52,211,153,0.4)",
              color: "#34d399", transition: "all 0.2s",
            }}>Share</button>
          )}
          {showShareModal && createPortal(<ShareModal event={event} onClose={() => setShowShareModal(false)} />, document.body)}
          {/* Share to Org button — org leadership can promote team events to org-wide */}
          {canShareToOrg && !event.is_org_wide && event.team && (
            <button onClick={() => onShareToOrg(event)} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
              textTransform: "uppercase", cursor: "pointer",
              padding: "6px 12px", background: "none",
              border: "1px solid rgba(52,211,153,0.4)", color: "#34d399", transition: "all 0.2s",
            }}>Share to Org</button>
          )}
          {isPast && (
            <span style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2,
              color: "rgba(255,255,255,0.25)", textTransform: "uppercase",
            }}>PAST EVENT</span>
          )}
          {/* View Details link — available on all events */}
          <Link to={`/events/${event.id}/review`} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1.5,
            textTransform: "uppercase", cursor: "pointer",
            padding: "6px 12px", background: "none",
            border: `1px solid ${ACCENT}33`, color: ACCENT,
            transition: "all 0.2s", textDecoration: "none",
          }}>View Details</Link>
          {/* View thread on Discord — only renders if this event has been posted to Discord */}
          <EventDiscordLinks eventId={event.id} compact />
          {/* Show review rating if available */}
          {reviewData && reviewData.overall_rating > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <StarRatingInline value={reviewData.overall_rating} size={12} color={ACCENT} />
              {reviewData.avg_member_rating > 0 && (
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>
                  · {reviewData.avg_member_rating}/4 member avg
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Payout Manager Modal ─────────────────────────────────────────────────────
// Shown to the event creator + org leadership after a contract event concludes.
// Lists every attendee with the auto-calculated amount owed, lets the leader
// tweak individual adjustments, assign bonus recipients, and check "paid" per
// attendee. Saves back to events.payouts + is_completed.
function PayoutManagerModal({ event, profile, onClose, onSave }) {
  const currency = getCompensationCurrency(event);
  const calc = calculateEventPayouts(event);
  const comp = event.compensation || {};
  const existingPayouts = Array.isArray(event.payouts) ? event.payouts : [];

  // Merge calculated rows with any saved payout state (preserves paid/confirmed flags)
  const initialRows = calc.rows.map(r => {
    const saved = existingPayouts.find(p => p.user_id === r.user_id) || {};
    return {
      user_id: r.user_id,
      username: r.username,
      avatar: r.avatar,
      role: r.role,
      group: r.group,
      rsvp_group: r.rsvp_group,
      calculated: r.total_owed,
      breakdown: r.breakdown,
      adjustment: saved.adjustment ?? 0,
      paid: saved.paid ?? false,
      paid_by: saved.paid_by ?? null,
      paid_at: saved.paid_at ?? null,
      confirmed: saved.confirmed ?? false,
      confirmed_at: saved.confirmed_at ?? null,
      notes: saved.notes ?? "",
    };
  });

  const [rows, setRows] = useState(initialRows);
  const [bonuses, setBonuses] = useState(Array.isArray(comp.bonuses) ? comp.bonuses.map(b => ({ ...b, recipients: Array.isArray(b.recipients) ? [...b.recipients] : [] })) : []);
  const [saving, setSaving] = useState(false);

  function updateRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }
  function togglePaid(idx) {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const nowPaid = !r.paid;
      return {
        ...r,
        paid: nowPaid,
        paid_by: nowPaid ? profile?.id : null,
        paid_at: nowPaid ? new Date().toISOString() : null,
      };
    }));
  }
  function toggleBonusRecipient(bonusIdx, userId) {
    setBonuses(prev => prev.map((b, i) => {
      if (i !== bonusIdx) return b;
      const recipients = Array.isArray(b.recipients) ? b.recipients : [];
      const has = recipients.includes(userId);
      return { ...b, recipients: has ? recipients.filter(r => r !== userId) : [...recipients, userId] };
    }));
  }

  // Recompute totals live when bonuses / adjustments change.
  const liveEvent = { ...event, compensation: { ...comp, bonuses } };
  const liveCalc = calculateEventPayouts(liveEvent);
  const rowsWithLiveCalc = rows.map(r => {
    const live = liveCalc.rows.find(lr => lr.user_id === r.user_id);
    const calculated = live ? live.total_owed : r.calculated;
    return { ...r, calculated, breakdown: live ? live.breakdown : r.breakdown };
  });
  const grandTotal = rowsWithLiveCalc.reduce((s, r) => s + (r.calculated + (Number(r.adjustment) || 0)), 0);
  const paidSoFar = rowsWithLiveCalc.filter(r => r.paid).reduce((s, r) => s + (r.calculated + (Number(r.adjustment) || 0)), 0);

  async function handleSave(markComplete) {
    setSaving(true);
    const payouts = rowsWithLiveCalc.map(r => ({
      user_id: r.user_id,
      username: r.username,
      calculated: r.calculated,
      adjustment: Number(r.adjustment) || 0,
      total_owed: r.calculated + (Number(r.adjustment) || 0),
      paid: !!r.paid,
      paid_by: r.paid_by || null,
      paid_at: r.paid_at || null,
      confirmed: !!r.confirmed,
      confirmed_at: r.confirmed_at || null,
      notes: r.notes || "",
    }));
    await onSave(event.id, {
      payouts,
      compensation: { ...comp, bonuses },
      ...(markComplete ? { is_completed: true, completed_at: new Date().toISOString() } : {}),
    });
    setSaving(false);
    onClose();
  }

  const labelStyle = {
    fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2,
    color: "rgba(232,234,246,0.4)", textTransform: "uppercase",
  };
  const inputStyle = {
    padding: "6px 10px", background: "rgba(255,255,255,0.04)",
    border: `1px solid ${ACCENT}33`, color: "#e8eaf6",
    fontFamily: "'Rajdhani', sans-serif", fontSize: 13, borderRadius: 2, outline: "none",
  };

  function fmt(n) {
    const num = Number(n) || 0;
    return num.toLocaleString();
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)",
      backdropFilter: "blur(8px)", display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "env(safe-area-inset-top, 12px) clamp(8px, 2vw, 24px) env(safe-area-inset-bottom, 12px)",
      overflowY: "auto",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "100%", maxWidth: 880, margin: "clamp(12px, 4vh, 40px) 0",
        background: "#0a0610", border: "1px solid rgba(52,211,153,0.44)",
        padding: "clamp(16px, 4vw, 36px) clamp(12px, 3vw, 32px)",
        position: "relative",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, width: 24, height: 24, borderTop: "2px solid #34d399", borderLeft: "2px solid #34d399" }} />

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "#34d399", textTransform: "uppercase", marginBottom: 4 }}>
              Contract Payout Manager
            </div>
            <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(14px, 2vw, 18px)", color: "#e8eaf6", lineHeight: 1.3 }}>
              {event.title}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 24, cursor: "pointer", padding: 8, minWidth: 36, minHeight: 36 }}>&times;</button>
        </div>

        {/* Totals */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 20 }}>
          <div style={{ padding: "10px 14px", border: "1px solid rgba(52,211,153,0.25)", background: "rgba(52,211,153,0.05)" }}>
            <div style={labelStyle}>Grand Total</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: "#34d399", marginTop: 4 }}>
              {fmt(grandTotal)} <span style={{ fontSize: 11, color: "rgba(52,211,153,0.7)" }}>{currency}</span>
            </div>
          </div>
          <div style={{ padding: "10px 14px", border: "1px solid rgba(252,214,87,0.25)", background: "rgba(252,214,87,0.05)" }}>
            <div style={labelStyle}>Paid So Far</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: ACCENT, marginTop: 4 }}>
              {fmt(paidSoFar)} <span style={{ fontSize: 11, color: "rgba(252,214,87,0.7)" }}>{currency}</span>
            </div>
          </div>
          <div style={{ padding: "10px 14px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}>
            <div style={labelStyle}>Remaining</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: "#e8eaf6", marginTop: 4 }}>
              {fmt(grandTotal - paidSoFar)} <span style={{ fontSize: 11, color: "rgba(232,234,246,0.5)" }}>{currency}</span>
            </div>
          </div>
          <div style={{ padding: "10px 14px", border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)" }}>
            <div style={labelStyle}>Contractors</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 20, color: "#e8eaf6", marginTop: 4 }}>
              {rowsWithLiveCalc.filter(r => r.paid).length} <span style={{ fontSize: 11, color: "rgba(232,234,246,0.5)" }}>/ {rowsWithLiveCalc.length} paid</span>
            </div>
          </div>
        </div>

        {/* Bonus recipient assignment */}
        {bonuses.length > 0 && (
          <div style={{ marginBottom: 20, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Assign Bonus Recipients</div>
            {bonuses.map((b, bi) => (
              <div key={bi} style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "#e8eaf6", marginBottom: 6 }}>
                  <strong>{b.name}</strong> — <span style={{ color: "#34d399" }}>{fmt(b.rate)} {currency}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {rowsWithLiveCalc.map(r => {
                    const has = Array.isArray(b.recipients) && b.recipients.includes(r.user_id);
                    return (
                      <button key={r.user_id} onClick={() => toggleBonusRecipient(bi, r.user_id)} style={{
                        fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, padding: "4px 10px", cursor: "pointer",
                        background: has ? "rgba(52,211,153,0.15)" : "transparent",
                        border: `1px solid ${has ? "rgba(52,211,153,0.5)" : "rgba(255,255,255,0.15)"}`,
                        color: has ? "#34d399" : "rgba(232,234,246,0.5)", transition: "all 0.2s",
                      }}>
                        {has ? "✓ " : "+ "}{r.username || "Unknown"}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Payout rows */}
        {rowsWithLiveCalc.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "rgba(255,255,255,0.2)", letterSpacing: 2 }}>
            NO ATTENDEES TO PAY
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rowsWithLiveCalc.map((r, idx) => {
              const total = r.calculated + (Number(r.adjustment) || 0);
              const breakdownParts = [];
              if (r.breakdown.flat) breakdownParts.push(`Flat ${fmt(r.breakdown.flat)}`);
              if (r.breakdown.group) breakdownParts.push(`${r.breakdown.group.name} ${fmt(r.breakdown.group.rate)}`);
              if (r.breakdown.position) breakdownParts.push(`${r.breakdown.position.name} ${fmt(r.breakdown.position.rate)}`);
              if (r.breakdown.rsvp) breakdownParts.push(`${r.breakdown.rsvp.name} ${fmt(r.breakdown.rsvp.rate)}`);
              (r.breakdown.bonuses || []).forEach(b => breakdownParts.push(`${b.name} ${fmt(b.rate)}`));
              return (
                <div key={r.user_id} style={{
                  padding: "12px 14px",
                  border: `1px solid ${r.paid ? "rgba(52,211,153,0.35)" : "rgba(255,255,255,0.08)"}`,
                  background: r.paid ? "rgba(52,211,153,0.05)" : "rgba(255,255,255,0.02)",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto auto auto",
                  gap: 12,
                  alignItems: "center",
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%", overflow: "hidden",
                    background: "#0d1120", border: "1px solid rgba(255,255,255,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {r.avatar
                      ? <img src={r.avatar} alt={r.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontFamily: "'Orbitron'", fontSize: 10, color: "rgba(232,234,246,0.5)" }}>{(r.username || "?").charAt(0)}</span>
                    }
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>
                      {r.username || "Unknown"}
                      {r.role && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginLeft: 8, letterSpacing: 1, textTransform: "uppercase" }}>· {r.role}</span>}
                      {r.group && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginLeft: 8, letterSpacing: 1, textTransform: "uppercase" }}>· {r.group}</span>}
                    </div>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", marginTop: 2 }}>
                      {breakdownParts.join(" + ") || "—"}
                    </div>
                    {r.confirmed && (
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "#34d399", marginTop: 3, letterSpacing: 1 }}>
                        ✓ CONTRACTOR CONFIRMED RECEIPT
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                    <div style={{ ...labelStyle, fontSize: 7 }}>Adjust</div>
                    <input
                      type="number"
                      value={r.adjustment || ""}
                      onChange={e => updateRow(idx, "adjustment", e.target.value)}
                      style={{ ...inputStyle, width: 80, textAlign: "right" }}
                      placeholder="0"
                    />
                  </div>
                  <div style={{ textAlign: "right", minWidth: 120 }}>
                    <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 16, color: r.paid ? "#34d399" : "#e8eaf6" }}>
                      {fmt(total)}
                    </div>
                    <div style={{ ...labelStyle, fontSize: 8 }}>{currency}</div>
                  </div>
                  <label style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer" }}>
                    <div onClick={() => togglePaid(idx)} style={{
                      width: 28, height: 28, border: `2px solid ${r.paid ? "#34d399" : "rgba(255,255,255,0.2)"}`,
                      background: r.paid ? "#34d399" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", transition: "all 0.2s",
                    }}>
                      {r.paid && <span style={{ color: "#0a0610", fontWeight: 700, fontSize: 16 }}>✓</span>}
                    </div>
                    <div style={{ ...labelStyle, fontSize: 7, color: r.paid ? "#34d399" : "rgba(232,234,246,0.4)" }}>PAID</div>
                  </label>
                </div>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
          <button onClick={() => handleSave(false)} disabled={saving} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
            padding: "12px 24px", background: "rgba(52,211,153,0.15)", border: "1px solid #34d399",
            color: "#34d399", transition: "all 0.2s", flex: "1 1 auto", minHeight: 44, opacity: saving ? 0.4 : 1,
          }}>{saving ? "Saving..." : "Save Payouts"}</button>
          {!event.is_completed && (
            <button onClick={() => handleSave(true)} disabled={saving} style={{
              fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
              padding: "12px 24px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`,
              color: ACCENT, transition: "all 0.2s", flex: "1 1 auto", minHeight: 44, opacity: saving ? 0.4 : 1,
            }}>Save & Mark Complete</button>
          )}
          <button onClick={onClose} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2,
            textTransform: "uppercase", cursor: "pointer",
            padding: "12px 24px", background: "none", flex: "1 1 auto", minHeight: 44,
            border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)",
          }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// "Nap Time" voice channel — idle/AFK time, excluded from all VC hour/activity
// aggregations across the app. Matched loosely (casing/spacing-insensitive).
function isNaptimeChannel(name) {
  return (name || "").replace(/\s+/g, "").toLowerCase() === "naptime";
}

// ─── Default RSVP options ─────────────────────────────────────────────────────
export const DEFAULT_RSVP_OPTIONS = [
  { emoji: "✅", name: "Attending", attending: true, reminders: true },
  { emoji: "🕥", name: "Late", attending: true, reminders: true },
  { emoji: "❌", name: "Not Attending", attending: false, reminders: false },
];

// ─── Ops readiness levels (see org briefing doc) ─────────────────────────────
export const OPS_READINESS_LEVELS = [
  { name: "Casual", emoji: "🟢", hint: "Low pressure. High fun." },
  { name: "Coordinated", emoji: "🟡", hint: "Focused, but still fun." },
  { name: "Serious", emoji: "🔴", hint: "Execution matters." },
];

// ─── Member search box (Commander / 2iC pickers) ─────────────────────────────
// Searches active org members (member_status = 'member') by Discord name.
// Selected value: { user_id, username, discord_id, avatar }
function MemberSearchInput({ placeholder, value, onSelect, profiles }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const q = query.trim().toLowerCase();
  const matches = q
    ? (profiles || [])
        .filter(p => p.member_status === "member")
        .filter(p =>
          (p.discord_username || "").toLowerCase().includes(q) ||
          (p.discord_handle || "").toLowerCase().includes(q))
        .slice(0, 8)
    : [];

  if (value) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2 }}>
        {value.avatar ? <img src={value.avatar} alt="" style={{ width: 20, height: 20, borderRadius: "50%" }} /> : <span>👤</span>}
        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6", flex: 1 }}>{value.username}</span>
        <button onClick={() => onSelect(null)} style={{ background: "none", border: "none", color: "rgba(239,83,80,0.6)", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: "0 2px" }}>&times;</button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, boxSizing: "border-box", outline: "none" }}
      />
      {open && matches.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "#0d1120", border: "1px solid rgba(255,255,255,0.15)", maxHeight: 200, overflowY: "auto" }}>
          {matches.map(p => (
            <div
              key={p.id}
              onMouseDown={() => {
                onSelect({
                  user_id: p.id,
                  username: p.discord_username || p.discord_handle || "",
                  discord_id: p.discord_id || null,
                  avatar: p.discord_avatar || "",
                });
                setQuery(""); setOpen(false);
              }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(252,214,87,0.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              {p.discord_avatar ? <img src={p.discord_avatar} alt="" style={{ width: 20, height: 20, borderRadius: "50%" }} /> : <span>👤</span>}
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "#e8eaf6" }}>{p.discord_username || p.discord_handle}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Common timezone list
const COMMON_TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "Pacific/Honolulu", "America/Phoenix",
  "America/Toronto", "America/Vancouver", "America/Halifax",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Asia/Tokyo", "Asia/Shanghai", "Asia/Kolkata", "Asia/Dubai",
  "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
  "America/Sao_Paulo", "America/Mexico_City", "Africa/Lagos", "Africa/Cairo",
];

// ─── Event Form Modal ──────────────────────────────────────────────────────────
export function EventFormModal({ event, teams, onSave, onClose, canCreateOrgEvent, profile }) {
  const isEditing = !!event?.id;
  const fileInputRef = useRef(null);
  // Active org members for the Commander / 2iC search boxes
  const { profiles: allProfiles } = useFleetData();
  const [form, setForm] = useState({
    title: event?.title || "",
    type: event?.type || "Combat",
    description: event?.description || "",
    date: event?.date || "",
    time: event?.time || "",
    tags: event?.tags ? event.tags.join(", ") : "",
    is_org_wide: event?.is_org_wide ?? (canCreateOrgEvent && teams.length === 0),
    team: event?.team || (teams.length === 1 ? teams[0] : ""),
    allow_sharing: true, // always shareable — toggle removed from the form
    reminder: event?.reminder || "discord",
    image_url: event?.image_url || "",
    rally_location: event?.rally_location || "",
    commander: event?.commander || null,
    second_in_command: event?.second_in_command || null,
    ops_readiness: event?.ops_readiness || "",
    event_timezone: event?.event_timezone || profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    rsvp_options: event?.rsvp_options || DEFAULT_RSVP_OPTIONS,
    groups: event?.groups || [],
    // ── Contract event fields ──
    is_contract: event?.is_contract || false,
    compensation: event?.compensation && typeof event.compensation === "object" ? {
      currency: event.compensation.currency || "aUEC",
      currency_custom: event.compensation.currency_custom || "",
      flat_rate: event.compensation.flat_rate || 0,
      group_rates: event.compensation.group_rates || {},
      position_rates: Array.isArray(event.compensation.position_rates) ? event.compensation.position_rates : [],
      rsvp_role_rates: event.compensation.rsvp_role_rates || {},
      bonuses: Array.isArray(event.compensation.bonuses) ? event.compensation.bonuses : [],
    } : {
      currency: "aUEC",
      currency_custom: "",
      flat_rate: 0,
      group_rates: {},
      position_rates: [],
      rsvp_role_rates: {},
      bonuses: [],
    },
  });
  const [newPosition, setNewPosition] = useState({ name: "", rate: "" });
  const [newBonus, setNewBonus] = useState({ name: "", rate: "", recipients: [] });
  const [newGroup, setNewGroup] = useState({ name: "", color: "#ef5350" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageMode, setImageMode] = useState(form.image_url && !form.image_url.includes("supabase") ? "url" : "upload");
  const [dragOver, setDragOver] = useState(false);
  const [bucketImages, setBucketImages] = useState([]);
  const [showBucketBrowser, setShowBucketBrowser] = useState(false);
  const [loadingBucket, setLoadingBucket] = useState(false);

  async function loadBucketImages() {
    setLoadingBucket(true);
    try {
      const { data, error } = await supabase.storage.from("event-images").list("", { limit: 100, sortBy: { column: "created_at", order: "desc" } });
      if (error) throw error;
      const images = (data || []).filter(f => f.name && !f.name.startsWith(".")).map(f => {
        const { data: urlData } = supabase.storage.from("event-images").getPublicUrl(f.name);
        return { name: f.name, url: urlData.publicUrl, created: f.created_at };
      });
      setBucketImages(images);
    } catch (e) {
      console.error("Failed to load bucket images:", e);
    }
    setLoadingBucket(false);
  }

  async function uploadImage(file) {
    if (!file) return;
    if (!ACCEPTED_MEDIA_TYPES.includes(file.type)) { alert("Unsupported file type. Use PNG, JPG, GIF, WEBP, MP4, or WEBM."); return; }
    const isVideo = file.type.startsWith("video/");
    const isGif = file.type === "image/gif";
    const maxSize = (isVideo || isGif) ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    if (file.size > maxSize) { alert(`File too large. Max ${isVideo || isGif ? "50MB" : "10MB"} for ${isVideo ? "videos" : isGif ? "GIFs" : "images"}.`); return; }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const optFile = await optimizeImageFile(file, { maxDim: 1400, quality: 0.85 });
      const { error } = await supabase.storage.from("event-images").upload(path, optFile, { contentType: optFile.type || file.type });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("event-images").getPublicUrl(path);
      setForm(p => ({ ...p, image_url: urlData.publicUrl }));
    } catch (e) {
      console.error("Media upload failed:", e);
      alert("Upload failed: " + (e.message || e.error || "Unknown error. Check that the storage bucket allows video MIME types."));
    }
    setUploading(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.type.startsWith("image/") || file.type.startsWith("video/"))) uploadImage(file);
  }

  function addRsvpOption() {
    setForm(p => ({ ...p, rsvp_options: [...p.rsvp_options, { emoji: "🔔", name: "", attending: false, reminders: false }] }));
  }

  // The default "Not Attending" option is locked: it cannot be renamed,
  // toggled, or removed. This guarantees the decline flow (no roster, no
  // reminder DMs, decline confirmation DM) always works — same logic as the
  // Discord buttons.
  function isLockedDecline(opt) {
    return (opt?.name || "").trim().toLowerCase() === "not attending";
  }

  function updateRsvpOption(index, field, value) {
    setForm(p => {
      const opts = [...p.rsvp_options];
      // Locked decline option: only the emoji may change
      if (isLockedDecline(opts[index]) && field !== "emoji") return p;
      opts[index] = { ...opts[index], [field]: value };
      return { ...p, rsvp_options: opts };
    });
  }

  function removeRsvpOption(index) {
    setForm(p => {
      if (isLockedDecline(p.rsvp_options[index])) return p; // cannot be removed
      return { ...p, rsvp_options: p.rsvp_options.filter((_, i) => i !== index) };
    });
  }

  async function handleSave() {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    // Strip empty compensation entries so we don't pollute the DB
    const compClean = {
      currency: form.compensation.currency || "aUEC",
      ...(form.compensation.currency === "Custom" && form.compensation.currency_custom.trim()
        ? { currency_custom: form.compensation.currency_custom.trim() }
        : {}),
      flat_rate: Number(form.compensation.flat_rate) || 0,
      group_rates: Object.fromEntries(
        Object.entries(form.compensation.group_rates || {})
          .filter(([, v]) => Number(v) > 0)
          .map(([k, v]) => [k, Number(v)])
      ),
      position_rates: (form.compensation.position_rates || [])
        .filter(p => p.name && Number(p.rate) > 0)
        .map(p => ({ name: p.name, rate: Number(p.rate) })),
      rsvp_role_rates: Object.fromEntries(
        Object.entries(form.compensation.rsvp_role_rates || {})
          .filter(([, v]) => Number(v) > 0)
          .map(([k, v]) => [k, Number(v)])
      ),
      bonuses: (form.compensation.bonuses || [])
        .filter(b => b.name && Number(b.rate) > 0)
        .map(b => ({ name: b.name, rate: Number(b.rate), recipients: Array.isArray(b.recipients) ? b.recipients : [] })),
    };
    await onSave({
      ...form,
      tags: form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      team: form.is_org_wide ? null : form.team || null,
      rsvp_options: (() => {
        // Force correct flags on the locked "Not Attending" option and make
        // sure it's always present — keeps the decline communication flow
        // (no roster, no reminders, decline DM) intact on every event.
        const opts = form.rsvp_options
          .filter(o => o.name.trim())
          .map(o => isLockedDecline(o) ? { ...o, attending: false, reminders: false } : o);
        if (!opts.some(isLockedDecline)) {
          opts.push({ emoji: "❌", name: "Not Attending", attending: false, reminders: false });
        }
        return opts;
      })(),
      is_contract: !!form.is_contract,
      compensation: form.is_contract ? compClean : {},
    }, isEditing ? event.id : null);
    setSaving(false);
    onClose();
  }

  const inputStyle = {
    width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)",
    border: `1px solid ${ACCENT}33`, color: "#e8eaf6",
    fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none",
    boxSizing: "border-box",
  };
  const selectStyle = { ...inputStyle, background: "#0a0610" };

  const formLabelStyle = {
    fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2,
    color: ACCENT, textTransform: "uppercase", display: "block", marginBottom: 6,
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)",
      backdropFilter: "blur(8px)", display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "env(safe-area-inset-top, 12px) clamp(8px, 2vw, 24px) env(safe-area-inset-bottom, 12px)",
      overflowY: "auto",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "100%", maxWidth: 560, margin: "clamp(12px, 4vh, 40px) 0",
        background: "#0a0610", border: `1px solid ${ACCENT}44`,
        padding: "clamp(16px, 4vw, 36px) clamp(12px, 3vw, 32px)",
        position: "relative",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, width: 24, height: 24, borderTop: `2px solid ${ACCENT}`, borderLeft: `2px solid ${ACCENT}` }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(14px, 2vw, 17px)", color: "#e8eaf6" }}>
            {isEditing ? "Edit Event" : "Create Event"}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 24, cursor: "pointer", padding: 8, minWidth: 36, minHeight: 36 }}>&times;</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Title</label>
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={inputStyle} placeholder="e.g. Large Scale Mining Op" />
        </div>

        {/* Event Image */}
        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Event Image</label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <button onClick={() => { setImageMode("upload"); setShowBucketBrowser(false); }} style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "4px 10px", cursor: "pointer",
              background: imageMode === "upload" && !showBucketBrowser ? `${ACCENT}22` : "transparent", border: `1px solid ${imageMode === "upload" && !showBucketBrowser ? ACCENT : "rgba(255,255,255,0.15)"}`,
              color: imageMode === "upload" && !showBucketBrowser ? ACCENT : "rgba(232,234,246,0.5)", transition: "all 0.2s",
            }}>UPLOAD</button>
            <button onClick={() => { setImageMode("url"); setShowBucketBrowser(false); }} style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "4px 10px", cursor: "pointer",
              background: imageMode === "url" ? `${ACCENT}22` : "transparent", border: `1px solid ${imageMode === "url" ? ACCENT : "rgba(255,255,255,0.15)"}`,
              color: imageMode === "url" ? ACCENT : "rgba(232,234,246,0.5)", transition: "all 0.2s",
            }}>EXTERNAL URL</button>
            <button onClick={() => { setShowBucketBrowser(true); setImageMode("upload"); if (bucketImages.length === 0) loadBucketImages(); }} style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "4px 10px", cursor: "pointer",
              background: showBucketBrowser ? `${ACCENT}22` : "transparent", border: `1px solid ${showBucketBrowser ? ACCENT : "rgba(255,255,255,0.15)"}`,
              color: showBucketBrowser ? ACCENT : "rgba(232,234,246,0.5)", transition: "all 0.2s",
            }}>BROWSE EXISTING</button>
          </div>
          {showBucketBrowser ? (
            <div style={{
              border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)",
              padding: 12, maxHeight: 240, overflowY: "auto",
            }}>
              {loadingBucket ? (
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: ACCENT, letterSpacing: 1 }}>Loading images...</span>
              ) : bucketImages.length === 0 ? (
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.3)", letterSpacing: 1 }}>No images uploaded yet</span>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: 8 }}>
                  {bucketImages.map(img => (
                    <div key={img.name} onClick={() => { setForm(p => ({ ...p, image_url: img.url })); setShowBucketBrowser(false); }}
                      style={{
                        cursor: "pointer", border: form.image_url === img.url ? `2px solid ${ACCENT}` : "1px solid rgba(255,255,255,0.1)",
                        background: form.image_url === img.url ? `${ACCENT}15` : "rgba(0,0,0,0.3)",
                        padding: 4, transition: "all 0.2s", aspectRatio: "1",
                        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                      }}>
                      <img src={img.url} alt={img.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                    </div>
                  ))}
                </div>
              )}
              <button onClick={loadBucketImages} style={{
                fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, padding: "4px 8px", cursor: "pointer",
                background: "none", border: "1px solid rgba(255,255,255,0.1)", color: "rgba(232,234,246,0.4)", marginTop: 8,
              }}>↻ Refresh</button>
            </div>
          ) : imageMode === "upload" ? (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? ACCENT : "rgba(255,255,255,0.15)"}`,
                background: dragOver ? `${ACCENT}08` : "rgba(255,255,255,0.02)",
                padding: "20px", textAlign: "center", cursor: "pointer", transition: "all 0.2s",
              }}
            >
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,video/quicktime" style={{ display: "none" }}
                onChange={e => { if (e.target.files?.[0]) uploadImage(e.target.files[0]); }} />
              {uploading ? (
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: ACCENT, letterSpacing: 1 }}>UPLOADING...</span>
              ) : form.image_url ? (
                <div>
                  {isVideoUrl(form.image_url) ? (
                    <video src={form.image_url} autoPlay loop muted playsInline style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", marginBottom: 8 }} />
                  ) : (
                    <img src={form.image_url} alt="Event" style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain", marginBottom: 8 }} />
                  )}
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", letterSpacing: 1 }}>
                    Click or drag to replace · <span style={{ color: "#ef5350", cursor: "pointer" }} onClick={e => { e.stopPropagation(); setForm(p => ({ ...p, image_url: "" })); }}>Remove</span>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.4)", letterSpacing: 1, marginBottom: 4 }}>
                    Drop media here or click to browse
                  </div>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)" }}>
                    PNG, JPG, GIF, WEBP · Max 10MB — GIF, MP4, WEBM · Max 50MB
                  </div>
                </div>
              )}
            </div>
          ) : (
            <input value={form.image_url} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} style={inputStyle}
              placeholder="https://example.com/image.png (supports GIF, MP4, WEBM)" />
          )}
          {imageMode === "url" && form.image_url && (
            isVideoUrl(form.image_url) ? (
              <video src={form.image_url} autoPlay loop muted playsInline style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", marginTop: 8, border: "1px solid rgba(255,255,255,0.1)" }} />
            ) : (
              <img src={form.image_url} alt="Preview" style={{ maxWidth: "100%", maxHeight: 120, objectFit: "contain", marginTop: 8, border: "1px solid rgba(255,255,255,0.1)" }}
                onError={e => { e.target.style.display = "none"; }} onLoad={e => { e.target.style.display = "block"; }} />
            )
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>Type</label>
            <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))} style={{ ...selectStyle, width: "100%", minWidth: 0 }}>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>Date</label>
            <input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} style={{ ...inputStyle, colorScheme: "dark", width: "100%", minWidth: 0 }} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>Time</label>
            <input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} style={{ ...inputStyle, colorScheme: "dark", width: "100%", minWidth: 0 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>Timezone</label>
            <select value={form.event_timezone} onChange={e => setForm(p => ({ ...p, event_timezone: e.target.value }))} style={{ ...selectStyle, width: "100%", minWidth: 0 }}>
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz.replace(/_/g, " ").replace(/^.*\//, "")}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Command structure — Commander, 2iC, Ops Readiness (org briefing format) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>Commander</label>
            <MemberSearchInput
              placeholder="Search members…"
              value={form.commander}
              onSelect={v => setForm(p => ({ ...p, commander: v }))}
              profiles={allProfiles}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>2iC (2nd in Command)</label>
            <MemberSearchInput
              placeholder="Search members…"
              value={form.second_in_command}
              onSelect={v => setForm(p => ({ ...p, second_in_command: v }))}
              profiles={allProfiles}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Ops Readiness Level</label>
          <select value={form.ops_readiness} onChange={e => setForm(p => ({ ...p, ops_readiness: e.target.value }))} style={{ ...selectStyle, width: "100%", minWidth: 0 }}>
            <option value="">— Not set —</option>
            {OPS_READINESS_LEVELS.map(l => (
              <option key={l.name} value={l.name}>{l.emoji} {l.name} — {l.hint}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Rally Location</label>
          <input value={form.rally_location} onChange={e => setForm(p => ({ ...p, rally_location: e.target.value }))} style={inputStyle}
            placeholder="e.g. Stanton / Crusader / Port Olisar" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Brief / Description</label>
          <textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} placeholder="Mission briefing..." />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Tags (comma separated)</label>
          <input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} style={inputStyle} placeholder="e.g. All Hands, Priority" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={formLabelStyle}>Reminder</label>
            <select value={form.reminder} onChange={e => setForm(p => ({ ...p, reminder: e.target.value }))} style={{ ...selectStyle, width: "100%", minWidth: 0 }}>
              <option value="discord">Discord</option><option value="email">Email</option><option value="both">Both</option><option value="none">None</option>
            </select>
          </div>
        </div>

        {/* RSVP Options */}
        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Discord RSVP Reactions</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {form.rsvp_options.map((opt, i) => (
              <div key={i} style={{
                display: "flex", gap: 8, alignItems: "center", padding: "10px 12px",
                border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", flexWrap: "wrap",
              }}>
                <input value={opt.emoji} onChange={e => updateRsvpOption(i, "emoji", e.target.value)}
                  style={{ width: 40, textAlign: "center", padding: "6px 4px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontSize: 18, borderRadius: 2, boxSizing: "border-box" }}
                  placeholder="😀" />
                {isLockedDecline(opt) ? (
                  <>
                    <div style={{ flex: 1, minWidth: 100, padding: "6px 10px", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14 }}>
                      {opt.name}
                    </div>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "rgba(239,83,80,0.7)", flexShrink: 0 }}>
                      DECLINE OPTION — ALWAYS OFF ROSTER, NO REMINDERS
                    </span>
                  </>
                ) : (
                  <>
                    <input value={opt.name} onChange={e => updateRsvpOption(i, "name", e.target.value)}
                      style={{ flex: 1, minWidth: 100, padding: "6px 10px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, boxSizing: "border-box" }}
                      placeholder="Option name (used as group)" />
                    <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", flexShrink: 0 }}>
                      <div onClick={() => updateRsvpOption(i, "attending", !opt.attending)} style={{
                        width: 32, height: 18, borderRadius: 9, position: "relative", cursor: "pointer", flexShrink: 0,
                        background: opt.attending ? "#34d399" : "rgba(255,255,255,0.1)", transition: "all 0.3s",
                      }}>
                        <div style={{ position: "absolute", top: 2, left: opt.attending ? 16 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
                      </div>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: opt.attending ? "#34d399" : "rgba(232,234,246,0.4)" }}>ATT</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", flexShrink: 0 }}>
                      <div onClick={() => updateRsvpOption(i, "reminders", !opt.reminders)} style={{
                        width: 32, height: 18, borderRadius: 9, position: "relative", cursor: "pointer", flexShrink: 0,
                        background: opt.reminders ? "#4fc3f7" : "rgba(255,255,255,0.1)", transition: "all 0.3s",
                      }}>
                        <div style={{ position: "absolute", top: 2, left: opt.reminders ? 16 : 2, width: 14, height: 14, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
                      </div>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: opt.reminders ? "#4fc3f7" : "rgba(232,234,246,0.4)" }}>DM</span>
                    </label>
                    <button onClick={() => removeRsvpOption(i)} style={{
                      background: "none", border: "none", color: "rgba(239,83,80,0.6)", fontSize: 16, cursor: "pointer", padding: "0 4px", lineHeight: 1, flexShrink: 0,
                    }}>&times;</button>
                  </>
                )}
              </div>
            ))}
            <button onClick={addRsvpOption} style={{
              fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "6px 12px", cursor: "pointer",
              background: "none", border: "1px dashed rgba(255,255,255,0.2)", color: "rgba(232,234,246,0.5)", transition: "all 0.2s",
            }}>+ Add RSVP Option</button>
          </div>
        </div>

        {/* Event Groups / Squads */}
        <div style={{ marginBottom: 16 }}>
          <label style={formLabelStyle}>Event Groups / Squads</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {form.groups.map(g => (
              <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", border: `1px solid ${g.color}44`, background: `${g.color}08` }}>
                <div style={{ width: 14, height: 14, borderRadius: 2, background: g.color, flexShrink: 0 }} />
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6", flex: 1 }}>{g.name}</span>
                <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: g.color }}>{g.color}</span>
                <button onClick={() => setForm(p => ({ ...p, groups: p.groups.filter(sg => sg.name !== g.name) }))} style={{ background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: 14, padding: "0 4px", lineHeight: 1 }}>&times;</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
              <input
                value={newGroup.name}
                onChange={e => setNewGroup(p => ({ ...p, name: e.target.value }))}
                style={{ ...inputStyle, flex: 1, minWidth: 120 }}
                placeholder="Group name (e.g. Alpha Squad)"
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!newGroup.name.trim() || form.groups.find(g => g.name === newGroup.name)) return;
                    setForm(p => ({ ...p, groups: [...p.groups, { ...newGroup }] }));
                    setNewGroup({ name: "", color: "#ef5350" });
                  }
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 200 }}>
                  {PRESET_COLORS.map(c => (
                    <div key={c} onClick={() => setNewGroup(p => ({ ...p, color: c }))}
                      style={{ width: 18, height: 18, borderRadius: 2, background: c, cursor: "pointer", border: newGroup.color === c ? "2px solid #fff" : "1px solid transparent", boxShadow: newGroup.color === c ? `0 0 5px ${c}` : "none" }} />
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="color" value={newGroup.color} onChange={e => setNewGroup(p => ({ ...p, color: e.target.value }))} style={{ width: 24, height: 24, padding: 0, border: "none", background: "none", cursor: "pointer" }} />
                  <input value={newGroup.color} onChange={e => setNewGroup(p => ({ ...p, color: e.target.value }))} style={{ ...inputStyle, width: 84, padding: "4px 8px", fontSize: 11, fontFamily: "'Space Mono', monospace" }} placeholder="#ffffff" />
                </div>
              </div>
              <button
                onClick={() => {
                  if (!newGroup.name.trim() || form.groups.find(g => g.name === newGroup.name)) return;
                  setForm(p => ({ ...p, groups: [...p.groups, { ...newGroup }] }));
                  setNewGroup({ name: "", color: "#ef5350" });
                }}
                style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5,
                  textTransform: "uppercase", cursor: "pointer", alignSelf: "flex-start", marginTop: 2,
                  padding: "8px 14px", background: `${newGroup.color}18`,
                  border: `1px solid ${newGroup.color}66`, color: newGroup.color, transition: "all 0.2s",
                }}
              >+ Add</button>
            </div>
          </div>
        </div>

        {/* ─── Contract Event toggle ─── */}
        <div style={{
          marginBottom: 16,
          padding: "12px 14px",
          border: `1px solid ${form.is_contract ? "#34d39966" : "rgba(255,255,255,0.08)"}`,
          background: form.is_contract ? "rgba(52,211,153,0.04)" : "rgba(255,255,255,0.02)",
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", minHeight: 44 }}>
            <div onClick={() => setForm(p => ({ ...p, is_contract: !p.is_contract }))} style={{
              width: 44, height: 24, borderRadius: 12,
              background: form.is_contract ? "#34d399" : "rgba(255,255,255,0.1)",
              position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0,
            }}>
              <div style={{ position: "absolute", top: 3, left: form.is_contract ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
            </div>
            <div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: form.is_contract ? "#34d399" : "rgba(232,234,246,0.7)", textTransform: "uppercase" }}>
                Contract Event
              </div>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 12, color: "rgba(232,234,246,0.4)", marginTop: 2 }}>
                Unlock compensation tracking + payout management for this event
              </div>
            </div>
          </label>

          {form.is_contract && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(52,211,153,0.15)" }}>
              {/* Currency */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                <div style={{ minWidth: 0 }}>
                  <label style={formLabelStyle}>Currency</label>
                  <select
                    value={form.compensation.currency}
                    onChange={e => setForm(p => ({ ...p, compensation: { ...p.compensation, currency: e.target.value } }))}
                    style={{ ...selectStyle, width: "100%", minWidth: 0 }}
                  >
                    <option value="aUEC">aUEC</option>
                    <option value="Merits">Merits</option>
                    <option value="Custom">Custom…</option>
                  </select>
                </div>
                {form.compensation.currency === "Custom" && (
                  <div style={{ minWidth: 0 }}>
                    <label style={formLabelStyle}>Custom Label</label>
                    <input
                      value={form.compensation.currency_custom}
                      onChange={e => setForm(p => ({ ...p, compensation: { ...p.compensation, currency_custom: e.target.value } }))}
                      style={inputStyle}
                      placeholder="e.g. USD, Credits, Ship Parts"
                    />
                  </div>
                )}
              </div>

              {/* Flat rate */}
              <div style={{ marginBottom: 14 }}>
                <label style={formLabelStyle}>Flat Rate <span style={{ color: "rgba(232,234,246,0.4)", letterSpacing: 0 }}>(paid to every attendee)</span></label>
                <input
                  type="number"
                  min="0"
                  value={form.compensation.flat_rate || ""}
                  onChange={e => setForm(p => ({ ...p, compensation: { ...p.compensation, flat_rate: e.target.value } }))}
                  style={inputStyle}
                  placeholder="0"
                />
              </div>

              {/* Group rates — one input per event group */}
              {form.groups.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={formLabelStyle}>Rate by Group <span style={{ color: "rgba(232,234,246,0.4)", letterSpacing: 0 }}>(stacks on flat rate)</span></label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {form.groups.map(g => (
                      <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 14, height: 14, borderRadius: 2, background: g.color, flexShrink: 0 }} />
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", minWidth: 120 }}>{g.name}</span>
                        <input
                          type="number" min="0"
                          value={form.compensation.group_rates[g.name] || ""}
                          onChange={e => setForm(p => ({ ...p, compensation: { ...p.compensation, group_rates: { ...p.compensation.group_rates, [g.name]: e.target.value } } }))}
                          style={{ ...inputStyle, flex: 1 }}
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RSVP role rates */}
              {form.rsvp_options.filter(o => o.attending && o.name.trim()).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={formLabelStyle}>Rate by RSVP Response <span style={{ color: "rgba(232,234,246,0.4)", letterSpacing: 0 }}>(e.g. extra for "On Time" vs "Late")</span></label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {form.rsvp_options.filter(o => o.attending && o.name.trim()).map(o => (
                      <div key={o.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6", minWidth: 120 }}>{o.emoji} {o.name}</span>
                        <input
                          type="number" min="0"
                          value={form.compensation.rsvp_role_rates[o.name] || ""}
                          onChange={e => setForm(p => ({ ...p, compensation: { ...p.compensation, rsvp_role_rates: { ...p.compensation.rsvp_role_rates, [o.name]: e.target.value } } }))}
                          style={{ ...inputStyle, flex: 1 }}
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Position rates (custom list) */}
              <div style={{ marginBottom: 14 }}>
                <label style={formLabelStyle}>Rate by Position <span style={{ color: "rgba(232,234,246,0.4)", letterSpacing: 0 }}>(matched to each attendee's role)</span></label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                  {form.compensation.position_rates.map((p, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        value={p.name}
                        onChange={e => setForm(prev => ({ ...prev, compensation: { ...prev.compensation, position_rates: prev.compensation.position_rates.map((pp, idx) => idx === i ? { ...pp, name: e.target.value } : pp) } }))}
                        style={{ ...inputStyle, flex: 2 }}
                        placeholder="Position (e.g. Lead Miner)"
                      />
                      <input
                        type="number" min="0"
                        value={p.rate}
                        onChange={e => setForm(prev => ({ ...prev, compensation: { ...prev.compensation, position_rates: prev.compensation.position_rates.map((pp, idx) => idx === i ? { ...pp, rate: e.target.value } : pp) } }))}
                        style={{ ...inputStyle, flex: 1 }}
                        placeholder="Rate"
                      />
                      <button
                        onClick={() => setForm(prev => ({ ...prev, compensation: { ...prev.compensation, position_rates: prev.compensation.position_rates.filter((_, idx) => idx !== i) } }))}
                        style={{ background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: 16, padding: "0 6px" }}
                      >&times;</button>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={newPosition.name} onChange={e => setNewPosition(p => ({ ...p, name: e.target.value }))} style={{ ...inputStyle, flex: 2 }} placeholder="+ New position" />
                  <input type="number" min="0" value={newPosition.rate} onChange={e => setNewPosition(p => ({ ...p, rate: e.target.value }))} style={{ ...inputStyle, flex: 1 }} placeholder="Rate" />
                  <button
                    onClick={() => {
                      if (!newPosition.name.trim() || !Number(newPosition.rate)) return;
                      setForm(prev => ({ ...prev, compensation: { ...prev.compensation, position_rates: [...prev.compensation.position_rates, { name: newPosition.name.trim(), rate: Number(newPosition.rate) }] } }));
                      setNewPosition({ name: "", rate: "" });
                    }}
                    style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", padding: "8px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.5)", color: "#34d399" }}
                  >Add</button>
                </div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.35)", marginTop: 6, letterSpacing: 1 }}>
                  Attendee's "role" field (set in Manage Crew) must match position name for the rate to apply.
                </div>
              </div>

              {/* Bonuses */}
              <div style={{ marginBottom: 6 }}>
                <label style={formLabelStyle}>Bonuses <span style={{ color: "rgba(232,234,246,0.4)", letterSpacing: 0 }}>(one-off — assign recipients after the event in the payout manager)</span></label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
                  {form.compensation.bonuses.map((b, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        value={b.name}
                        onChange={e => setForm(prev => ({ ...prev, compensation: { ...prev.compensation, bonuses: prev.compensation.bonuses.map((bb, idx) => idx === i ? { ...bb, name: e.target.value } : bb) } }))}
                        style={{ ...inputStyle, flex: 2 }}
                        placeholder="Bonus name (e.g. First Kill)"
                      />
                      <input
                        type="number" min="0"
                        value={b.rate}
                        onChange={e => setForm(prev => ({ ...prev, compensation: { ...prev.compensation, bonuses: prev.compensation.bonuses.map((bb, idx) => idx === i ? { ...bb, rate: e.target.value } : bb) } }))}
                        style={{ ...inputStyle, flex: 1 }}
                        placeholder="Rate"
                      />
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", letterSpacing: 1, minWidth: 80 }}>
                        {Array.isArray(b.recipients) ? b.recipients.length : 0} recipient{(Array.isArray(b.recipients) ? b.recipients.length : 0) !== 1 ? "s" : ""}
                      </span>
                      <button
                        onClick={() => setForm(prev => ({ ...prev, compensation: { ...prev.compensation, bonuses: prev.compensation.bonuses.filter((_, idx) => idx !== i) } }))}
                        style={{ background: "none", border: "none", color: "#ef5350", cursor: "pointer", fontSize: 16, padding: "0 6px" }}
                      >&times;</button>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input value={newBonus.name} onChange={e => setNewBonus(p => ({ ...p, name: e.target.value }))} style={{ ...inputStyle, flex: 2 }} placeholder="+ New bonus" />
                  <input type="number" min="0" value={newBonus.rate} onChange={e => setNewBonus(p => ({ ...p, rate: e.target.value }))} style={{ ...inputStyle, flex: 1 }} placeholder="Rate" />
                  <button
                    onClick={() => {
                      if (!newBonus.name.trim() || !Number(newBonus.rate)) return;
                      setForm(prev => ({ ...prev, compensation: { ...prev.compensation, bonuses: [...prev.compensation.bonuses, { name: newBonus.name.trim(), rate: Number(newBonus.rate), recipients: [] }] } }));
                      setNewBonus({ name: "", rate: "", recipients: [] });
                    }}
                    style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", padding: "8px 14px", background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.5)", color: "#34d399" }}
                  >Add</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Scope toggles */}
        <div style={{ display: "flex", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
          {canCreateOrgEvent && (
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", minHeight: 44 }}>
              <div onClick={() => setForm(p => ({ ...p, is_org_wide: !p.is_org_wide }))} style={{
                width: 44, height: 24, borderRadius: 12,
                background: form.is_org_wide ? ACCENT : "rgba(255,255,255,0.1)",
                position: "relative", transition: "all 0.3s", cursor: "pointer", flexShrink: 0,
              }}>
                <div style={{ position: "absolute", top: 3, left: form.is_org_wide ? 22 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.3s" }} />
              </div>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.6)", textTransform: "uppercase" }}>Org-Wide</span>
            </label>
          )}
        </div>

        {/* Team picker (when not org-wide) */}
        {!form.is_org_wide && teams.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={formLabelStyle}>Team</label>
            <select value={form.team} onChange={e => setForm(p => ({ ...p, team: e.target.value }))} style={selectStyle}>
              <option value="">Select team...</option>
              {teams.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
          <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.date} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
            padding: "12px 24px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`,
            color: ACCENT, transition: "all 0.2s", flex: "1 1 auto", minHeight: 44,
            opacity: (saving || !form.title.trim() || !form.date) ? 0.4 : 1,
          }}>{saving ? "Saving..." : isEditing ? "Save Changes" : "Create Event"}</button>
          <button onClick={onClose} style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2,
            textTransform: "uppercase", cursor: "pointer",
            padding: "12px 24px", background: "none", flex: "1 1 auto", minHeight: 44,
            border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)",
          }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Org Availability view ─────────────────────────────────────────────────────
// A clean, at-a-glance heatmap of when the org (or the selected team) prefers to
// play, built from each member's profile availability windows and converted into
// the viewer's timezone. Darker = more members free in that hour. Reached via the
// "Availability" button next to Calendar / List.
const AVAIL_ORG_WIDE_TABS = new Set(["all", "my-events", "org", "contracts"]);
export function OrgAvailabilityView({ profiles, activeTab, tabLabel, viewerTz }) {
  const ACCENT = "#fcd657";
  const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  function getOffsetHours(tz) {
    try {
      const date = new Date();
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: false }).formatToParts(date);
      const p = {};
      parts.forEach(part => { p[part.type] = parseInt(part.value, 10); });
      const tzMs = Date.UTC(p.year, p.month - 1, p.day, p.hour === 24 ? 0 : p.hour, p.minute, p.second);
      return Math.round((tzMs - date.getTime()) / 3600000 * 4) / 4;
    } catch { return 0; }
  }
  function normalizeWindows(val) {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(w => w && typeof w.start === "number" && typeof w.end === "number");
    if (typeof val === "object" && val.all_day) return [{ start: 0, end: 24 }];
    if (typeof val === "object" && typeof val.start === "number" && typeof val.end === "number") return [val];
    return [];
  }
  function fmtHour(h) {
    const hh = ((h % 24) + 24) % 24;
    if (hh === 0) return "12a";
    if (hh === 12) return "12p";
    return hh < 12 ? `${hh}a` : `${hh - 12}p`;
  }

  const isOrgWide = AVAIL_ORG_WIDE_TABS.has(activeTab);
  const scoped = (profiles || []).filter(p => {
    if (p.member_status && p.member_status !== "member") return false;
    if (isOrgWide) return true;
    const memberTeams = Array.isArray(p.teams) ? p.teams : (p.team ? [p.team] : []);
    return memberTeams.includes(activeTab) || p.team === activeTab;
  });
  const withAvailability = scoped.filter(p => p.availability && Object.keys(p.availability).length > 0);

  const viewerOffset = getOffsetHours(viewerTz);
  // grid[day][hour] = { count, names[] } in the viewer's timezone
  const grid = DAYS.map(() => Array.from({ length: 24 }, () => ({ count: 0, names: [] })));
  withAvailability.forEach(member => {
    const shift = viewerOffset - getOffsetHours(member.timezone || "UTC");
    const name = member.discord_username || member.discord_handle || "Unknown";
    DAYS.forEach((day, di) => {
      normalizeWindows(member.availability?.[day]).forEach(win => {
        let s, e;
        if (win.start === 0 && win.end === 24) { s = 0; e = 24; }
        else {
          s = ((win.start + shift) % 24 + 24) % 24;
          e = ((win.end + shift) % 24 + 24) % 24;
          if (e <= s) e += 24;
        }
        for (let h = Math.floor(s); h < Math.ceil(e); h++) {
          const cell = grid[di][h % 24];
          cell.count++;
          if (cell.names.length < 40) cell.names.push(name);
        }
      });
    });
  });
  let max = 0;
  grid.forEach(row => row.forEach(c => { if (c.count > max) max = c.count; }));

  // Peak window per day (longest run at the day's own max) for the summary line
  const hourLabels = [0, 3, 6, 9, 12, 15, 18, 21];

  if (withAvailability.length === 0) {
    return (
      <div style={{ padding: "clamp(40px, 8vw, 80px) 20px", textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>
        No availability set for {tabLabel} yet
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 16, fontWeight: 700, color: "#e8eaf6", letterSpacing: 1 }}>
          Preferred Availability — {tabLabel}
        </div>
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
          {withAvailability.length} member{withAvailability.length !== 1 ? "s" : ""} · darker = more free
        </div>
      </div>
      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.4)", marginBottom: 18 }}>
        Times in your timezone: <span style={{ color: ACCENT }}>{viewerTz}</span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "44px repeat(7, minmax(70px, 1fr))", gap: 3, minWidth: 620 }}>
          {/* header row */}
          <div />
          {DAY_LABELS.map(d => (
            <div key={d} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.5)", textAlign: "center", textTransform: "uppercase", paddingBottom: 4 }}>{d}</div>
          ))}
          {/* 24 hour rows */}
          {Array.from({ length: 24 }, (_, h) => (
            <React.Fragment key={h}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)", textAlign: "right", paddingRight: 6, display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                {h % 3 === 0 ? fmtHour(h) : ""}
              </div>
              {DAY_LABELS.map((_, di) => {
                const cell = grid[di][h];
                const intensity = max > 0 ? cell.count / max : 0;
                const isPeak = cell.count > 0 && cell.count === max;
                return (
                  <div
                    key={di}
                    title={cell.count > 0 ? `${DAY_LABELS[di]} ${fmtHour(h)} — ${cell.count} free: ${cell.names.slice(0, 12).join(", ")}${cell.names.length > 12 ? "…" : ""}` : `${DAY_LABELS[di]} ${fmtHour(h)} — none`}
                    style={{
                      height: 16,
                      background: cell.count > 0 ? `rgba(252,214,87,${0.1 + intensity * 0.85})` : "rgba(255,255,255,0.02)",
                      border: isPeak ? `1px solid ${ACCENT}` : "1px solid rgba(255,255,255,0.04)",
                      borderRadius: 2,
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.4)", textTransform: "uppercase" }}>
        <span>Fewer</span>
        {[0, 0.2, 0.4, 0.6, 0.8, 1].map(i => (
          <div key={i} style={{ width: 18, height: 12, background: i === 0 ? "rgba(255,255,255,0.02)" : `rgba(252,214,87,${0.1 + i * 0.85})`, border: "1px solid rgba(255,255,255,0.05)" }} />
        ))}
        <span>More</span>
        <span style={{ marginLeft: 14, color: "rgba(232,234,246,0.55)" }}>Peak: {max} member{max !== 1 ? "s" : ""} free at once</span>
      </div>
    </div>
  );
}

// ─── Main Events Page ──────────────────────────────────────────────────────────
// ─── Month-grid Calendar view ─────────────────────────────────────────────
// Renders a 6×7 month grid. Each cell shows a dot per event scheduled for that day,
// colored by affiliation: GOLD = org-wide, green = contract, team color otherwise.
// Visibility/access is enforced upstream — `events` already passes through the active-tab
// filter (getVisibleEvents), so we never need to know about user perms in here.
export function EventsCalendar({ events, month, onMonthChange, selectedDay, onSelectDay, teams, peakHoursByDow, vcActivityByDow, viewerTz }) {
  // Day-of-week keys aligned with the Sun-Sat header strip below
  const DOW_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  function fmtHourCompact(h) {
    const hr = Math.round(((h % 24) + 24) % 24);
    if (hr === 0) return "12a";
    if (hr === 12) return "12p";
    return hr < 12 ? `${hr}a` : `${hr - 12}p`;
  }

  // Cache offsets per (date, tz) so we only call Intl.DateTimeFormat once per pair.
  const _offsetCache = new Map();
  function getOffsetHoursAt(tz, atDate) {
    const key = `${tz}|${atDate.toDateString()}`;
    if (_offsetCache.has(key)) return _offsetCache.get(key);
    try {
      const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: false }).formatToParts(atDate);
      const o = {};
      parts.forEach(p => { o[p.type] = parseInt(p.value, 10); });
      const tzMs = Date.UTC(o.year, o.month - 1, o.day, o.hour === 24 ? 0 : o.hour, o.minute, o.second);
      const off = Math.round((tzMs - atDate.getTime()) / 3600000 * 4) / 4;
      _offsetCache.set(key, off);
      return off;
    } catch { return 0; }
  }
  // Convert an event's creator-tz hour:minute into the viewer's local hour:minute.
  // Returns { hour, minute, dayShift } where dayShift is -1, 0, or 1 if the event
  // crosses midnight relative to viewer's day. We use dayShift = 0 (i.e. always
  // position on the calendar's date cell) since the cell IS the date — but the
  // hour/minute reflects the viewer's local clock at that moment.
  function eventTimeInViewerTz(ev) {
    const t = String(ev.time || "12:00").match(/^(\d{1,2}):(\d{2})/);
    const h = t ? Math.min(23, parseInt(t[1], 10)) : 12;
    const m = t ? Math.min(59, parseInt(t[2], 10)) : 0;
    const creatorTz = ev.event_timezone || ev.creator_timezone || null;
    if (!creatorTz || !viewerTz || creatorTz === viewerTz || !ev.date) return { hour: h, minute: m };
    try {
      const at = new Date(`${ev.date}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
      const cOff = getOffsetHoursAt(creatorTz, at);
      const vOff = getOffsetHoursAt(viewerTz, at);
      const shift = vOff - cOff;
      let hour = h + Math.floor(shift);
      let minute = m + Math.round((shift - Math.floor(shift)) * 60);
      if (minute >= 60) { hour += 1; minute -= 60; }
      if (minute < 0) { hour -= 1; minute += 60; }
      hour = ((hour % 24) + 24) % 24;
      return { hour, minute };
    } catch { return { hour: h, minute: m }; }
  }
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  // Bucket events by date string for O(1) cell lookup
  const eventsByDate = {};
  events.forEach(ev => {
    if (!ev.date) return;
    if (!eventsByDate[ev.date]) eventsByDate[ev.date] = [];
    eventsByDate[ev.date].push(ev);
  });

  function eventColor(ev) {
    if (ev.is_contract) return "#34d399";
    if (ev.is_org_wide) return GOLD;
    if (ev.team) {
      const t = (teams || []).find(x => x.name === ev.team);
      if (t?.color) return t.color;
    }
    return "rgba(255,255,255,0.5)";
  }

  // Build the 42-cell grid (6 weeks × 7 days), starting on the Sunday on/before the 1st
  const monthFirst = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(monthFirst);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const dStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    cells.push({ date: d, dStr, inMonth: d.getMonth() === month.getMonth() });
  }

  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const navBtnStyle = {
    fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 1,
    background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)",
    color: "rgba(232,234,246,0.7)", padding: "6px 14px", cursor: "pointer",
    transition: "all 0.15s", textTransform: "uppercase",
  };

  function navMonth(delta) {
    const m = new Date(month);
    m.setDate(1);
    m.setMonth(m.getMonth() + delta);
    onMonthChange(m);
  }

  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", padding: "clamp(12px, 2vw, 20px)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700, color: "#e8eaf6", letterSpacing: 1 }}>{monthLabel}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => navMonth(-1)} aria-label="Previous month" style={navBtnStyle}>‹</button>
          <button onClick={() => onMonthChange(new Date(today.getFullYear(), today.getMonth(), 1))} style={navBtnStyle}>Today</button>
          <button onClick={() => navMonth(1)} aria-label="Next month" style={navBtnStyle}>›</button>
        </div>
      </div>

      {/* Weekday strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.35)", textAlign: "center", textTransform: "uppercase", padding: "6px 0" }}>{d}</div>
        ))}
      </div>

      {/* Optional team-activity strip — one mini 24h bar per weekday with peak hours highlighted.
          Only renders when the parent computes and passes a peakHoursByDow map. */}
      {peakHoursByDow && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 8 }}>
          {DOW_KEYS.map(dow => {
            const peak = peakHoursByDow[dow];
            const tooltip = peak
              ? `Peak activity: ${fmtHourCompact(peak.start)}–${fmtHourCompact(peak.end)}${peak.count ? ` (${peak.count} member${peak.count === 1 ? "" : "s"})` : ""}`
              : "No availability data";
            return (
              <div key={dow} title={tooltip} style={{ padding: "0 4px" }}>
                <div style={{ position: "relative", height: 5, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden" }}>
                  {peak && (
                    <div style={{
                      position: "absolute",
                      left: `${(peak.start / 24) * 100}%`,
                      width: `${((peak.end - peak.start) / 24) * 100}%`,
                      top: 0, bottom: 0,
                      background: "#f59e0b",
                      boxShadow: "0 0 4px #f59e0bcc",
                    }} />
                  )}
                </div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 0.5, color: peak ? "#f59e0baa" : "rgba(232,234,246,0.18)", marginTop: 2, textAlign: "center", lineHeight: 1, height: 8 }}>
                  {peak ? `${fmtHourCompact(peak.start)}–${fmtHourCompact(peak.end)}` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Day cells */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map(cell => {
          const dayEvents = eventsByDate[cell.dStr] || [];
          const isToday = cell.dStr === todayStr;
          const isSelected = cell.dStr === selectedDay;
          const hasEvents = dayEvents.length > 0;
          return (
            <button
              key={cell.dStr}
              onClick={() => onSelectDay(isSelected ? null : cell.dStr)}
              style={{
                background: isSelected ? `${ACCENT}22` : hasEvents ? `${ACCENT}06` : (isToday ? "rgba(255,255,255,0.04)" : "transparent"),
                border: `1px solid ${isSelected ? ACCENT : isToday ? `${ACCENT}88` : hasEvents ? `${ACCENT}33` : "rgba(255,255,255,0.05)"}`,
                color: cell.inMonth ? "#e8eaf6" : "rgba(232,234,246,0.25)",
                fontFamily: "'Rajdhani', sans-serif",
                cursor: "pointer",
                padding: "8px 6px",
                // Fixed height so every cell — empty or full — is identical. Excess events clip and
                // surface as the "+N more" indicator.
                height: "clamp(80px, 11vw, 120px)",
                display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start",
                position: "relative", transition: "all 0.15s",
                opacity: cell.inMonth ? 1 : 0.5,
                textAlign: "left",
                overflow: "hidden",
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = `${ACCENT}10`; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = hasEvents ? `${ACCENT}06` : (isToday ? "rgba(255,255,255,0.04)" : "transparent"); }}
            >
              {/* Date number — always anchored to the top-left of every cell */}
              <div style={{
                position: "absolute", top: 4, left: 6, zIndex: 2,
                fontSize: "clamp(12px, 1.6vw, 15px)",
                fontWeight: isToday ? 800 : 500,
                lineHeight: 1,
                color: isToday ? ACCENT : (cell.inMonth ? "#e8eaf6" : "rgba(232,234,246,0.25)"),
                textShadow: isToday ? `0 0 6px ${ACCENT}66` : "none",
                pointerEvents: "none",
              }}>{cell.date.getDate()}</div>

              {/* Activity strip — vertical 24h heatmap on the right edge.
                  Only renders when the parent passed vcActivityByDow. Top row = 12am,
                  bottom row = 11pm. Cell intensity comes from VC activity for this day-of-week. */}
              {vcActivityByDow && (() => {
                const dow = cell.date.getDay();
                const hours = vcActivityByDow[dow] || [];
                const max = vcActivityByDow.__max || 0;
                if (max <= 0) return null;
                return (
                  <div style={{ position: "absolute", top: 4, right: 4, bottom: 4, width: 6, display: "flex", flexDirection: "column", gap: 0, pointerEvents: "none" }}>
                    {hours.map((v, h) => {
                      const intensity = max > 0 ? v / max : 0;
                      return (
                        <div key={h} title={`${h === 0 ? "12a" : h === 12 ? "12p" : h < 12 ? `${h}a` : `${h - 12}p`} — ${(v / 3600).toFixed(1)}h VC`}
                          style={{ flex: 1, background: intensity > 0 ? `rgba(252,214,87,${0.06 + intensity * 0.85})` : "transparent" }} />
                      );
                    })}
                  </div>
                );
              })()}

              {/* Events positioned by start time. The cell's vertical axis maps 12am→top
                  to 11:59pm→bottom; each event sits at its start hour's offset. Right padding
                  leaves room for the activity strip. */}
              {hasEvents && (() => {
                const reserveTop = 18;            // space for the date number
                const stripPad = vcActivityByDow ? 14 : 4; // right reserved for activity strip + gap
                return (
                  <div style={{ position: "absolute", top: reserveTop, left: 4, right: stripPad, bottom: 4, pointerEvents: "none" }}>
                    {dayEvents.map((ev, i) => {
                      const c = eventColor(ev);
                      // Convert the event's creator-timezone time to the viewer's local time
                      // before positioning, so a 9pm Eastern event lands at 6pm on a Pacific viewer's strip.
                      const { hour: h, minute: m } = eventTimeInViewerTz(ev);
                      const frac = (h + m / 60) / 24;
                      const topPct = frac * 100;
                      return (
                        <div key={ev.id || i} className="cal-event-pill" title={`${ev.name || "Event"} · ${h % 12 || 12}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`}
                          style={{
                            position: "absolute", top: `${topPct}%`, left: 0, right: 0,
                            transform: "translateY(-50%)",
                            display: "flex", alignItems: "center", gap: 4,
                            padding: "1px 4px",
                            background: `${c}1f`,
                            borderLeft: `2px solid ${c}`,
                            fontFamily: "'Space Mono', monospace",
                            fontSize: "clamp(8px, 0.9vw, 10px)",
                            color: "#e8eaf6",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                          }}>
                          <span className="cal-event-dot" style={{ width: 5, height: 5, borderRadius: "50%", background: c, boxShadow: `0 0 4px ${c}aa`, flexShrink: 0 }} />
                          <span className="cal-event-label" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{ev.name || ev.title || "Event"}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.05)", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(232,234,246,0.45)", textTransform: "uppercase" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: GOLD }} /> Org-wide</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399" }} /> Contract</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "rgba(255,255,255,0.5)" }} /> Team</span>
        {peakHoursByDow && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 14, height: 4, background: "#f59e0b", boxShadow: "0 0 4px #f59e0bcc" }} /> Peak Hours</span>
        )}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const { eventId: deepLinkEventId } = useParams();
  const { user, profile } = useAuth();
  const { events: preloadedEvents, teams: preloadedTeams, profiles: preloadedProfiles, loaded: preloaded, refreshEvents } = useFleetData();
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [managingEvent, setManagingEvent] = useState(null);
  const [payoutEvent, setPayoutEvent] = useState(null);
  const [reviewDataMap, setReviewDataMap] = useState({}); // { [event_id]: { overall_rating, avg_member_rating } }
  const [userReminders, setUserReminders] = useState({}); // { [event_id]: [{ reminder_type, remind_at }] }
  const [toast, setToast] = useState(null); // { message, type: "success"|"error" }

  // Calendar/list view state — defaults to calendar; user can toggle to the legacy list.
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "list"
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null); // "YYYY-MM-DD" or null

  // Hourly VC activity bucketed by viewer's local day-of-week + hour. Used by the
  // calendar to render a thin activity-heatmap strip down the right side of each cell.
  // We fetch the last 90 days of `left` events from voice_activity_events and aggregate
  // client-side so the buckets land in the viewer's timezone.
  const [vcActivityByDow, setVcActivityByDow] = useState(null);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const since = new Date();
      since.setDate(since.getDate() - 90);
      const { data, error } = await supabase
        .from("voice_activity_events")
        .select("event_at, session_seconds, channel_name")
        .eq("action", "left")
        .not("session_seconds", "is", null)
        .gte("event_at", since.toISOString())
        .limit(50000);
      if (cancelled) return;
      if (error || !data) return;
      const grid = Array.from({ length: 7 }, () => new Array(24).fill(0));
      data.forEach(ev => {
        if (isNaptimeChannel(ev.channel_name)) return; // exclude idle/AFK nap time
        const d = new Date(ev.event_at);
        grid[d.getDay()][d.getHours()] += ev.session_seconds || 0;
      });
      let max = 0;
      grid.forEach(row => row.forEach(v => { if (v > max) max = v; }));
      // Stash max alongside the rows so each cell can normalize without re-walking.
      grid.__max = max;
      setVcActivityByDow(grid);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const perms = profile ? getEventPerms(profile) : { isOrgAdmin: false, isOrgLeadership: false, userTeams: [], ledTeams: [] };

  // Use preloaded data if available, otherwise fetch
  useEffect(() => {
    if (preloaded && preloadedEvents.length >= 0) {
      setEvents(preloadedEvents);
      setTeams(preloadedTeams);
      setLoading(false);
    } else {
      loadData();
    }
  }, [profile, preloaded, preloadedEvents, preloadedTeams]);

  // Load review data for past events
  useEffect(() => {
    if (events.length === 0) return;
    async function loadReviews() {
      try {
        const [{ data: reviews }, { data: ratings }] = await Promise.all([
          supabase.from("event_reviews").select("event_id, overall_rating"),
          supabase.from("event_ratings").select("event_id, rating"),
        ]);
        const map = {};
        (reviews || []).forEach(r => {
          map[r.event_id] = { overall_rating: r.overall_rating, avg_member_rating: 0 };
        });
        // Compute averages
        const ratingsByEvent = {};
        (ratings || []).forEach(r => {
          if (!ratingsByEvent[r.event_id]) ratingsByEvent[r.event_id] = [];
          ratingsByEvent[r.event_id].push(r.rating);
        });
        Object.entries(ratingsByEvent).forEach(([eid, rats]) => {
          const avg = (rats.reduce((s, r) => s + r, 0) / rats.length).toFixed(1);
          if (!map[eid]) map[eid] = { overall_rating: 0, avg_member_rating: 0 };
          map[eid].avg_member_rating = parseFloat(avg);
        });
        setReviewDataMap(map);
      } catch (e) { /* tables may not exist yet */ }
    }
    loadReviews();
  }, [events]);

  // If user isn't logged in, fall back from "my-events" to "org"
  useEffect(() => {
    if (profile === null && activeTab === "my-events") setActiveTab("org");
  }, [profile, activeTab]);

  // Deep-link: auto-switch to correct tab and scroll when navigating from Discord
  const [highlightedEventId, setHighlightedEventId] = useState(deepLinkEventId || null);
  useEffect(() => {
    if (!deepLinkEventId || events.length === 0) return;
    const target = events.find(e => e.id === deepLinkEventId);
    if (target) {
      // Switch to the right tab
      if (target.is_contract) setActiveTab("contracts");
      else if (target.is_org_wide) setActiveTab("org");
      else if (target.team) setActiveTab(target.team);
      // Scroll to event after a small delay for rendering
      setTimeout(() => {
        const el = document.getElementById(`event-${deepLinkEventId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      // Clear highlight after 3 seconds
      setTimeout(() => setHighlightedEventId(null), 3000);
    }
  }, [deepLinkEventId, events]);

  // Load user's reminder subscriptions with schedule details
  useEffect(() => {
    if (!profile) return;
    async function loadReminders() {
      try {
        const { data } = await supabase
          .from("event_reminders")
          .select("event_id, reminder_type, remind_at")
          .eq("user_id", profile.id)
          .eq("sent", false);
        const map = {};
        (data || []).forEach(r => {
          if (!map[r.event_id]) map[r.event_id] = [];
          map[r.event_id].push({ type: r.reminder_type, remind_at: r.remind_at });
        });
        setUserReminders(map);
      } catch (e) { /* table may not exist yet */ }
    }
    loadReminders();
  }, [profile, events]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function showToast(message, type = "success") {
    setToast({ message, type });
  }

  // 30-second polling for live attendee updates + piggyback reminder dispatch
  useEffect(() => {
    // Trigger reminder check on page load and every 5 minutes while page is open
    const fireReminders = () => {
      fetch("/api/send-reminders", { method: "GET" }).catch(() => {});
    };
    fireReminders();
    const reminderInterval = setInterval(fireReminders, 5 * 60 * 1000);

    const interval = setInterval(() => {
      refreshEvents();
    }, 30000);
    return () => { clearInterval(interval); clearInterval(reminderInterval); };
  }, [refreshEvents]);

  async function loadData() {
    setLoading(true);
    const [{ data: eventsData }, { data: teamsData }] = await Promise.all([
      supabase.from("events").select("*").order("date"),
      supabase.from("teams").select("id, name, color, discord_channel_id, discord_test_mode, discord_test_user_id").order("name"),
    ]);
    setEvents(eventsData || []);
    setTeams(teamsData || []);
    setLoading(false);
  }

  // Filter events based on active tab and permissions (exclude archived)
  function getVisibleEvents() {
    if (!events) return [];
    // Never show archived events on the events page
    const active = events.filter(e => !e.archived);

    if (activeTab === "all") {
      // Everything the user can see across every tab — for non-admins this means
      // org-wide + contract + their own teams' events. Admins see all team events.
      const visibleTeamNames = perms.isOrgAdmin
        ? new Set(teams.map(t => t.name))
        : new Set(perms.userTeams);
      return active.filter(e => {
        if (e.is_org_wide) return true;
        if (e.is_contract) return profile != null;
        if (e.team) return visibleTeamNames.has(e.team);
        return false;
      });
    }

    if (activeTab === "org") {
      return active.filter(e => e.is_org_wide);
    }

    if (activeTab === "my-events") {
      if (!profile) return [];
      return active.filter(e => isUserAttending(e.attendees, profile.id));
    }

    if (activeTab === "contracts") {
      return active.filter(e => e.is_contract);
    }

    // Team-specific tab
    const teamName = activeTab;
    return active.filter(e => e.team === teamName && !e.is_org_wide);
  }

  function canCreateForTab() {
    if (!profile) return false;
    if (activeTab === "my-events") return false;
    // Contract events: any logged-in user can create
    if (activeTab === "contracts") return true;
    // Org events tab: only org leadership (sort 1.0–2.9) / super admins can create
    if (activeTab === "org") return perms.isOrgAdmin;
    // Team tab: org leadership can create for any team, team leads for their own team
    if (perms.isOrgAdmin) return true;
    return perms.ledTeams.includes(activeTab);
  }

  function canEditEvent(event) {
    if (!profile) return false;
    // Org leadership / super admins can edit any event (org or team)
    if (perms.isOrgAdmin) return true;
    // Team leads can edit events for their team
    if (event.team && perms.ledTeams.includes(event.team)) return true;
    // Event creator can always edit their own event
    if (event.created_by === profile.id) return true;
    return false;
  }

  function getCreatableTeams() {
    if (perms.isOrgAdmin) return teams.map(t => t.name);
    return perms.ledTeams;
  }

  async function saveEvent(formData, eventId) {
    if (eventId) {
      await supabase.from("events").update(formData).eq("id", eventId);
      // If team event, update the Discord channel post (or DM in test mode)
      if (formData.team) {
        try {
          const teamData = teams.find(t => t.name === formData.team);
          if (teamData?.discord_channel_id) {
            if (teamData.discord_test_mode && teamData.discord_test_user_id) {
              const res = await fetch("/api/discord-notify", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "send_dm", discord_id: teamData.discord_test_user_id, event: { ...formData, id: eventId }, dm_type: "event_update" }),
              });
              showToast(res.ok ? "✓ Test DM sent — event update" : "✗ Test DM failed", res.ok ? "success" : "error");
            } else if (!teamData.discord_test_mode) {
              const { data: post } = await supabase.from("event_discord_posts").select("*").eq("event_id", eventId).single();
              if (post) {
                const res = await fetch("/api/discord-notify", {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ action: "update_event", channel_id: post.channel_id, message_id: post.message_id, event: { ...formData, id: eventId } }),
                });
                showToast(res.ok ? "✓ Discord channel post updated" : "✗ Discord update failed", res.ok ? "success" : "error");
              }
            }
          }
        } catch (e) { console.warn("Discord update failed:", e); showToast("✗ Discord update failed", "error"); }
      }
    } else {
      const { data: newEvent } = await supabase.from("events").insert([{ ...formData, created_by: profile.id, attendees: [], creator_timezone: profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone }]).select().single();
      // Post new event to the team's Discord channel (or DM in test mode)
      if (newEvent && formData.team) {
        try {
          const teamData = teams.find(t => t.name === formData.team);
          if (teamData?.discord_channel_id) {
            if (teamData.discord_test_mode && teamData.discord_test_user_id) {
              const res = await fetch("/api/discord-notify", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "send_dm", discord_id: teamData.discord_test_user_id, event: newEvent, dm_type: "event_post" }),
              });
              showToast(res.ok ? "✓ Test DM sent — new event" : "✗ Test DM failed to send", res.ok ? "success" : "error");
            } else if (!teamData.discord_test_mode) {
              const res = await fetch("/api/discord-notify", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "post_event", channel_id: teamData.discord_channel_id, event: newEvent }),
              });
              const result = await res.json();
              if (result.message_id) {
                await supabase.from("event_discord_posts").insert([{ event_id: newEvent.id, channel_id: teamData.discord_channel_id, message_id: result.message_id, thread_id: result.thread_id || null }]);
                showToast("✓ Event posted to Discord channel", "success");
              } else {
                showToast("✗ Failed to post to Discord channel", "error");
              }
            }
          }
        } catch (e) { console.warn("Discord post failed:", e); showToast("✗ Discord post failed", "error"); }
      }
    }
    refreshEvents();
  }

  // Archive event instead of deleting — moves it out of the events page
  async function archiveEvent(id) {
    if (!window.confirm("Archive this event? It will be removed from the events page but can be restored from the Admin Center.")) return;
    await supabase.from("events").update({ archived: true }).eq("id", id);
    refreshEvents();
  }

  // Toggle RSVP — uses structured attendee objects
  async function toggleRSVP(event, selectedOption) {
    if (!profile) return;
    const attendees = normalizeAttendees(event.attendees);
    const existing = attendees.find(a => a.user_id === profile.id);

    let updated;
    // If selectedOption is null and user is attending, or selectedOption is non-attending — remove
    const isLeaving = (selectedOption === null && existing) || (selectedOption && !selectedOption.attending);
    // If selectedOption is attending — add or update group
    const isJoining = selectedOption && selectedOption.attending;

    if (isLeaving) {
      // Remove attendee and clean up their reminders
      updated = attendees.filter(a => a.user_id !== profile.id);
      // Remove reminder subscriptions when leaving
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event_id: event.id, user_id: profile.id }),
        });
        setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
      } catch (e) { /* non-critical */ }
      // Send decline confirmation DM when explicitly marking a non-attending
      // option (parity with the Discord button flow, which DMs on declines too).
      // If they previously held an attending RSVP, the DM alerts them that
      // they have been removed from the event.
      if (selectedOption && !selectedOption.attending && profile.discord_id) {
        const prevOpt = existing?.rsvp_group
          ? (event.rsvp_options || []).find(o => o.name === existing.rsvp_group)
          : null;
        const wasAttending = existing ? (prevOpt ? prevOpt.attending !== false : true) : false;
        if (wasAttending) showToast(`You have been removed from "${event.title}"`, "success");
        try {
          await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "send_dm", discord_id: profile.discord_id, event,
              dm_type: "decline_confirm", rsvp_option_name: selectedOption.name,
              was_attending: wasAttending,
            }),
          });
        } catch (e) { /* non-critical */ }
      }
    } else if (isJoining) {
      if (existing) {
        // Already attending — update rsvp_group
        updated = attendees.map(a => a.user_id === profile.id ? { ...a, rsvp_group: selectedOption.name } : a);
      } else {
        // Add with profile info and rsvp_group
        updated = [...attendees, {
          user_id: profile.id,
          username: profile.discord_username || "",
          avatar: profile.discord_avatar || "",
          role: "",
          group: "",
          rsvp_group: selectedOption.name,
          sort_order: null,
        }];
      }
      // Auto-subscribe to reminders when joining (if option has reminders enabled)
      if (!existing && selectedOption.reminders && profile.discord_id && event.date) {
        try {
          const remRes = await fetch("/api/event-reminders", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event_id: event.id,
              user_id: profile.id,
              discord_id: profile.discord_id,
              event_date: event.date,
              event_time: event.time || "00:00",
            }),
          });
          const remData = await remRes.json();
          const schedule = (remData.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
          setUserReminders(prev => ({ ...prev, [event.id]: schedule.length > 0 ? schedule : [{ type: "pending", remind_at: "" }] }));
        } catch (e) { /* non-critical */ }
      }
      // Send signup confirmation DM (only on first join, not group change)
      if (!existing && profile.discord_id) {
        try {
          await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "send_dm", discord_id: profile.discord_id, event, dm_type: "signup_confirm" }),
          });
        } catch (e) { /* non-critical */ }
      }
    } else {
      // Simple toggle (no option selected, no rsvp_options on event)
      if (existing) {
        updated = attendees.filter(a => a.user_id !== profile.id);
        try {
          await fetch("/api/event-reminders", {
            method: "DELETE", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ event_id: event.id, user_id: profile.id }),
          });
          setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
        } catch (e) { /* non-critical */ }
      } else {
        updated = [...attendees, {
          user_id: profile.id,
          username: profile.discord_username || "",
          avatar: profile.discord_avatar || "",
          role: "",
          group: "",
          sort_order: null,
        }];
        if (profile.discord_id) {
          try {
            await fetch("/api/discord-notify", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "send_dm", discord_id: profile.discord_id, event, dm_type: "signup_confirm" }),
            });
          } catch (e) { /* non-critical */ }
        }
      }
    }
    await supabase.from("events").update({ attendees: updated }).eq("id", event.id);
    // Update all Discord embeds for this event with the latest RSVP roster
    try {
      fetch("/api/discord-notify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_rsvp_embed", event_id: event.id }),
      });
    } catch (e) { /* non-critical */ }
    refreshEvents();
  }

  // Toggle reminder subscription — supports individual types or clear-all
  // reminderType: specific type like "1hr", "24hr", etc. or null for clear-all
  // isRemoving: true to remove, false to add
  async function toggleReminder(event, reminderType, isRemoving) {
    if (!profile || !profile.discord_id) return;

    if (isRemoving) {
      // Remove a specific type or all reminders
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_id: event.id,
            user_id: profile.id,
            ...(reminderType ? { reminder_type: reminderType } : {}),
          }),
        });
        if (reminderType) {
          // Remove just this type from local state
          setUserReminders(prev => {
            const existing = (prev[event.id] || []).filter(r => r.type !== reminderType);
            if (existing.length === 0) { const next = { ...prev }; delete next[event.id]; return next; }
            return { ...prev, [event.id]: existing };
          });
          showToast("Reminder removed", "success");
        } else {
          // Clear all
          setUserReminders(prev => { const next = { ...prev }; delete next[event.id]; return next; });
          showToast("All reminders cleared", "success");
        }
      } catch (e) { /* non-critical */ }
    } else {
      // Add a specific reminder type
      try {
        const res = await fetch("/api/event-reminders", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_id: event.id,
            user_id: profile.id,
            discord_id: profile.discord_id,
            event_date: event.date,
            event_time: event.time || "00:00",
            ...(reminderType ? { reminder_type: reminderType } : {}),
          }),
        });
        const data = await res.json().catch(() => null);
        const newReminders = (data?.reminders || []).map(r => ({ type: r.reminder_type, remind_at: r.remind_at }));
        setUserReminders(prev => {
          const existing = prev[event.id] || [];
          // Merge new reminders with existing, avoiding duplicates
          const merged = [...existing];
          for (const nr of newReminders) {
            if (!merged.some(m => m.type === nr.type)) merged.push(nr);
          }
          return { ...prev, [event.id]: merged };
        });
        const labels = { at_start: "on event start", "10min": "10 minutes before", "1hr": "1 hour before", "24hr": "1 day before", "1week": "1 week before" };
        showToast(`🔔 Reminder set — ${labels[reminderType] || "before this event"}`, "success");
      } catch (e) { /* non-critical */ }
    }
  }

  // Save attendee management changes (role, group, sort_order)
  async function saveAttendees(eventId, updatedAttendees) {
    await supabase.from("events").update({ attendees: updatedAttendees }).eq("id", eventId);
    // Update all Discord embeds with latest RSVP roster
    try {
      fetch("/api/discord-notify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_rsvp_embed", event_id: eventId }),
      });
    } catch (e) { /* non-critical */ }
    refreshEvents();
  }

  // Save payout ledger + completion flags for a contract event
  async function savePayouts(eventId, updates) {
    await supabase.from("events").update(updates).eq("id", eventId);
    refreshEvents();
  }

  // Share a team event to the org (moves it to org-wide)
  async function shareToOrg(event) {
    if (!window.confirm(`Share "${event.title}" to the entire org? It will move to the Org Events tab.`)) return;
    await supabase.from("events").update({ is_org_wide: true }).eq("id", event.id);
    refreshEvents();
  }

  // Build tab list based on user permissions:
  // - My Events: first, visible to everyone logged in
  // - Org Events: second, visible to everyone
  // - Team tabs: only for members of that team OR org leadership (sort 1.0–2.9)
  const tabList = [];

  // "All Events" is always first — it shows every event the user has access to.
  tabList.push({ id: "all", label: "All Events", color: ACCENT });

  if (profile) {
    tabList.push({ id: "my-events", label: "My Events", color: GOLD, isGold: true });
  }

  tabList.push({ id: "org", label: "Org Events", color: ACCENT });

  // Contract Events tab — visible to anyone logged in, right after Org Events
  if (profile) {
    tabList.push({ id: "contracts", label: "Contract Events", color: "#34d399" });
  }

  if (profile) {
    // Org leadership (1.0–2.9) and super admins see ALL team tabs
    // Regular members only see tabs for teams they belong to
    const userTeamNames = perms.isOrgAdmin
      ? teams.map(t => t.name)
      : perms.userTeams;

    userTeamNames.forEach(teamName => {
      const teamData = teams.find(t => t.name === teamName);
      tabList.push({
        id: teamName,
        label: teamName,
        color: teamData?.color || "#ce93d8",
      });
    });
  }


  const visibleEvents = getVisibleEvents();
  const upcomingEvents = visibleEvents.filter(e => !e.date || new Date(`${e.date}T${e.time || "23:59"}`) >= new Date());
  const pastEvents = visibleEvents.filter(e => e.date && new Date(`${e.date}T${e.time || "23:59"}`) < new Date());

  // ── Peak activity hours for the current view, converted to viewer's timezone ─
  // Aggregates profiles that match the active filter:
  //   - "all" / "my-events" / "org" / "contracts" → every org member with availability
  //   - team name (e.g. "Mining", "Unit 99")     → only members of that team
  // The calendar's amber peak strip surfaces hours when the most matching members
  // are simultaneously available.
  const peakHoursByDow = (() => {
    const allProfiles = preloadedProfiles || [];
    const ORG_WIDE_TABS = new Set(["all", "my-events", "org", "contracts"]);
    const isOrgWide = ORG_WIDE_TABS.has(activeTab);
    // Team membership matches the same shape TeamPage uses: profiles.teams[] OR profiles.team
    const sourceProfiles = isOrgWide
      ? allProfiles
      : allProfiles.filter(p => {
          const memberTeams = Array.isArray(p.teams) ? p.teams : (p.team ? [p.team] : []);
          return memberTeams.includes(activeTab) || p.team === activeTab;
        });
    const withAvailability = sourceProfiles.filter(p => p.availability && Object.keys(p.availability).length > 0);
    if (withAvailability.length === 0) return null;

    // Viewer TZ: profile setting first, browser fallback otherwise — so anyone (logged in or not) sees something sensible.
    const viewerTz = profile?.timezone || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC");

    function getOffsetHours(tz) {
      try {
        const date = new Date();
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hour12: false }).formatToParts(date);
        const p = {};
        parts.forEach(part => { p[part.type] = parseInt(part.value, 10); });
        const tzMs = Date.UTC(p.year, p.month - 1, p.day, p.hour === 24 ? 0 : p.hour, p.minute, p.second);
        return Math.round((tzMs - date.getTime()) / 3600000 * 4) / 4;
      } catch { return 0; }
    }
    function normalizeWindows(val) {
      if (!val) return [];
      if (Array.isArray(val)) return val.filter(w => w && typeof w.start === 'number' && typeof w.end === 'number');
      if (typeof val === 'object' && val.all_day) return [{ start: 0, end: 24 }];
      if (typeof val === 'object' && typeof val.start === 'number' && typeof val.end === 'number') return [val];
      return [];
    }

    const viewerOffset = getOffsetHours(viewerTz);
    const DAYS = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
    const result = {};
    DAYS.forEach(day => {
      const counts = new Array(24).fill(0);
      withAvailability.forEach(member => {
        const memberOffset = getOffsetHours(member.timezone || "UTC");
        const shift = viewerOffset - memberOffset;
        const wins = normalizeWindows(member.availability?.[day]);
        wins.forEach(win => {
          if (win.start === 0 && win.end === 24) {
            for (let h = 0; h < 24; h++) counts[h]++;
            return;
          }
          let s = ((win.start + shift) % 24 + 24) % 24;
          let e = ((win.end + shift) % 24 + 24) % 24;
          if (e <= s) e += 24;
          for (let h = Math.floor(s); h < Math.ceil(e); h++) counts[h % 24]++;
        });
      });
      const max = Math.max(...counts);
      if (max === 0) { result[day] = null; return; }
      let bestStart = 0, bestLen = 0, curStart = -1, curLen = 0;
      for (let h = 0; h < 24; h++) {
        if (counts[h] === max) {
          if (curStart === -1) curStart = h;
          curLen++;
          if (curLen > bestLen) { bestStart = curStart; bestLen = curLen; }
        } else { curStart = -1; curLen = 0; }
      }
      result[day] = { start: bestStart, end: bestStart + bestLen, count: max };
    });
    return result;
  })();

  return (
    <div style={{
      fontFamily: "'Rajdhani', 'Orbitron', sans-serif",
      background: "#05070f", color: "#e8eaf6",
      minHeight: "100vh", overflowX: "hidden",
    }}>
      <StarField />

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: "fixed", top: 80, right: 24, zIndex: 9999,
          padding: "14px 24px", borderRadius: 4,
          background: toast.type === "error" ? "rgba(244,67,54,0.95)" : "rgba(76,175,80,0.95)",
          color: "#fff", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600,
          letterSpacing: 0.5, boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          animation: "toastSlideIn 0.3s ease-out",
          display: "flex", alignItems: "center", gap: 10, maxWidth: 380,
          cursor: "pointer",
        }} onClick={() => setToast(null)}>
          <span>{toast.message}</span>
        </div>
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #fcd657; color: #05070f; }
        a { text-decoration: none; color: inherit; }
        .nav-link {
          font-family: 'Orbitron', sans-serif;
          font-size: 11px; letter-spacing: 2.5px;
          text-transform: uppercase; color: rgba(232,234,246,0.55);
          position: relative; transition: color 0.25s;
        }
        .nav-link:hover { color: #fcd657; }
        .nav-link::after {
          content: ''; position: absolute; bottom: -4px;
          left: 0; width: 0; height: 1px;
          background: #fcd657; transition: width 0.3s;
        }
        .nav-link:hover::after { width: 100%; }
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(40px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .events-tab {
          cursor: pointer; transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .events-tab:hover {
          opacity: 1 !important;
        }
        .events-tabs-scroll {
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgba(252,214,87,0.3) transparent;
          scroll-behavior: smooth;
        }
        .events-tabs-scroll::-webkit-scrollbar { height: 4px; }
        .events-tabs-scroll::-webkit-scrollbar-track { background: transparent; }
        .events-tabs-scroll::-webkit-scrollbar-thumb {
          background: rgba(252,214,87,0.25);
          border-radius: 2px;
        }
        .events-tabs-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(252,214,87,0.5);
        }
        @media (max-width: 600px) {
          .events-tabs-scroll { scrollbar-width: none; }
          .events-tabs-scroll::-webkit-scrollbar { display: none; }
        }
        /* Calendar cells are too tight for text labels on narrow viewports —
           collapse to dot-only mode so the day still gets visual emphasis. */
        @media (max-width: 720px) {
          .cal-event-pill .cal-event-label { display: none; }
          .cal-event-pill { justify-content: center; padding: 2px !important; border-left: none !important; background: transparent !important; }
          .cal-event-pill .cal-event-dot { width: 9px !important; height: 9px !important; }
          .cal-event-more { text-align: center; padding-left: 0 !important; }
        }
      `}</style>

      <Nav accentColor={ACCENT} />

      {/* Header */}
      <section style={{
        position: "relative", zIndex: 1,
        paddingTop: "calc(clamp(100px, 14vw, 140px) + env(safe-area-inset-top, 0px))",
        paddingBottom: "clamp(30px, 5vw, 50px)",
        paddingLeft: "clamp(16px, 4vw, 40px)",
        paddingRight: "clamp(16px, 4vw, 40px)",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
            <div style={{ width: 60, height: 2, background: ACCENT }} />
            <span style={{
              fontFamily: "'Space Mono', monospace", fontSize: 10,
              letterSpacing: 4, color: ACCENT, textTransform: "uppercase",
            }}>Command Board</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
            <h1 style={{
              fontFamily: "'Orbitron', sans-serif", fontWeight: 900,
              fontSize: "clamp(28px, 5vw, 48px)", color: "#e8eaf6", lineHeight: 1.1,
            }}>Events</h1>
            {canCreateForTab() && (
              <button
                onClick={() => { setEditingEvent(activeTab === "contracts" ? { is_contract: true } : null); setShowForm(true); }}
                style={{
                  fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
                  letterSpacing: 2, textTransform: "uppercase", cursor: "pointer",
                  padding: "10px 24px", background: `${ACCENT}18`,
                  border: `1px solid ${ACCENT}`, color: ACCENT,
                  transition: "all 0.2s",
                }}
              >
                + Create Event
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Filter dropdown + Calendar / List view toggle */}
      <section style={{ position: "relative", zIndex: 1, paddingLeft: "clamp(16px, 4vw, 40px)", paddingRight: "clamp(16px, 4vw, 40px)", paddingTop: 18, borderBottom: "1px solid rgba(255,255,255,0.07)", paddingBottom: 18 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
          {/* Category dropdown — replaces the old horizontal tab strip */}
          {(() => {
            const activeTabObj = tabList.find(t => t.id === activeTab) || tabList[0];
            const dropdownColor = activeTabObj.isGold ? GOLD : (activeTabObj.color || ACCENT);
            return (
              <div style={{ position: "relative", display: "inline-block" }}>
                <select
                  value={activeTab}
                  onChange={e => { setActiveTab(e.target.value); setSelectedDay(null); }}
                  style={{
                    appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
                    fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700,
                    letterSpacing: 2, textTransform: "uppercase",
                    background: `${dropdownColor}12`, color: dropdownColor,
                    border: `1px solid ${dropdownColor}`,
                    padding: "10px 38px 10px 18px", cursor: "pointer", minWidth: 200,
                    transition: "all 0.2s",
                  }}
                >
                  {tabList.map(tab => (
                    <option key={tab.id} value={tab.id} style={{ background: "#05070f", color: "#e8eaf6" }}>
                      {tab.isGold ? "★ " : ""}{tab.label}
                    </option>
                  ))}
                </select>
                {/* Custom caret — pointer-events:none so the click goes through to the select */}
                <span aria-hidden style={{
                  position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
                  pointerEvents: "none", color: dropdownColor, fontSize: 10,
                }}>▼</span>
              </div>
            );
          })()}
          <div style={{ display: "flex", gap: 6 }}>
          {[
            { id: "calendar", label: "Calendar" },
            { id: "list", label: "List" },
            { id: "availability", label: "Availability" },
          ].map(v => {
            const active = viewMode === v.id;
            return (
              <button key={v.id} onClick={() => setViewMode(v.id)} style={{
                background: active ? `${ACCENT}18` : "transparent",
                border: `1px solid ${active ? ACCENT : "rgba(255,255,255,0.15)"}`,
                color: active ? ACCENT : "rgba(232,234,246,0.55)",
                fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: active ? 700 : 400,
                letterSpacing: 2, textTransform: "uppercase",
                padding: "8px 18px", cursor: "pointer", transition: "all 0.2s",
              }}>{v.label}</button>
            );
          })}
          </div>
        </div>
      </section>

      {/* Events list / calendar */}
      <section style={{
        position: "relative", zIndex: 1,
        padding: "clamp(24px, 4vw, 40px) clamp(16px, 4vw, 40px)",
        minHeight: "50vh",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {loading ? (
            <div style={{
              fontFamily: "'Space Mono', monospace", fontSize: 11,
              color: "rgba(255,255,255,0.3)", letterSpacing: 2,
              textAlign: "center", padding: 80,
            }}>LOADING EVENTS...</div>
          ) : viewMode === "calendar" ? (
            <>
              <EventsCalendar
                events={visibleEvents}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
                teams={teams}
                peakHoursByDow={peakHoursByDow}
                vcActivityByDow={vcActivityByDow}
                viewerTz={profile?.timezone || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null)}
              />
              {selectedDay && (() => {
                const dayEvents = visibleEvents.filter(e => e.date === selectedDay);
                const dayLabel = new Date(`${selectedDay}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
                return (
                  <div style={{ marginTop: 28 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ width: 24, height: 2, background: ACCENT }} />
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: ACCENT, textTransform: "uppercase" }}>{dayLabel}</span>
                      </div>
                      <button onClick={() => setSelectedDay(null)} style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.45)", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", padding: "6px 12px", cursor: "pointer", textTransform: "uppercase" }}>Close</button>
                    </div>
                    {dayEvents.length === 0 ? (
                      <div style={{ padding: 28, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5, color: "rgba(232,234,246,0.3)", border: "1px dashed rgba(255,255,255,0.06)", textTransform: "uppercase" }}>No events on this day</div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {dayEvents.map(ev => {
                          const teamObj = ev.team ? teams.find(t => t.name === ev.team) : null;
                          const badge = ev.is_contract
                            ? { label: "Contract", color: "#34d399" }
                            : ev.is_org_wide
                              ? { label: "Org-wide", color: GOLD }
                              : ev.team
                                ? { label: ev.team, color: teamObj?.color || ACCENT }
                                : null;
                          return (
                            <div key={ev.id} style={{ position: "relative" }}>
                              {badge && (
                                <div style={{
                                  position: "absolute", top: -8, left: 12, zIndex: 2,
                                  fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2,
                                  textTransform: "uppercase", padding: "2px 8px",
                                  background: "#05070f", border: `1px solid ${badge.color}`,
                                  color: badge.color,
                                }}>{badge.label}</div>
                              )}
                              <EventCard
                                event={ev}
                                canEdit={canEditEvent(ev)}
                                canJoin={canJoinEvent(profile, ev, perms)}
                                onEdit={(e) => { setEditingEvent(e); setShowForm(true); }}
                                onArchive={archiveEvent}
                                onRSVP={toggleRSVP}
                                isAttending={isUserAttending(ev.attendees, profile?.id)}
                                profile={profile}
                                onManageAttendees={(e) => setManagingEvent(e)}
                                onShareToOrg={shareToOrg}
                                canShareToOrg={perms.isOrgAdmin}
                                reviewData={reviewDataMap[ev.id]}
                                hasReminder={!!userReminders[ev.id]}
                                reminderSchedule={userReminders[ev.id] || null}
                                onToggleReminder={toggleReminder}
                                isHighlighted={highlightedEventId === ev.id}
                                onManagePayouts={(e) => setPayoutEvent(e)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              {visibleEvents.length === 0 && (
                <div style={{ textAlign: "center", padding: "clamp(28px, 5vw, 48px) 20px", marginTop: 14, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(232,234,246,0.3)", textTransform: "uppercase" }}>
                  {activeTab === "my-events" ? "No events signed up for yet" : "No events on the schedule"}
                </div>
              )}
            </>
          ) : viewMode === "availability" ? (
            <OrgAvailabilityView
              profiles={preloadedProfiles || []}
              activeTab={activeTab}
              tabLabel={(tabList.find(t => t.id === activeTab) || {}).label || "the org"}
              viewerTz={profile?.timezone || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC")}
            />
          ) : (
            <>
              {/* Upcoming */}
              {upcomingEvents.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 24, height: 2, background: "rgba(255,255,255,0.15)" }} />
                    <span style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 9,
                      letterSpacing: 3, color: "rgba(232,234,246,0.35)", textTransform: "uppercase",
                    }}>Upcoming</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {upcomingEvents.map(ev => (
                      <EventCard
                        key={ev.id}
                        event={ev}
                        canEdit={canEditEvent(ev)}
                        canJoin={canJoinEvent(profile, ev, perms)}
                        onEdit={(e) => { setEditingEvent(e); setShowForm(true); }}
                        onArchive={archiveEvent}
                        onRSVP={toggleRSVP}
                        isAttending={isUserAttending(ev.attendees, profile?.id)}
                        profile={profile}
                        onManageAttendees={(e) => setManagingEvent(e)}
                        onShareToOrg={shareToOrg}
                        canShareToOrg={perms.isOrgAdmin}
                        reviewData={reviewDataMap[ev.id]}
                        hasReminder={!!userReminders[ev.id]}
                        reminderSchedule={userReminders[ev.id] || null}
                        onToggleReminder={toggleReminder}
                        isHighlighted={highlightedEventId === ev.id}
                        onManagePayouts={(e) => setPayoutEvent(e)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Past */}
              {pastEvents.length > 0 && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 24, height: 2, background: "rgba(255,255,255,0.08)" }} />
                    <span style={{
                      fontFamily: "'Space Mono', monospace", fontSize: 9,
                      letterSpacing: 3, color: "rgba(232,234,246,0.2)", textTransform: "uppercase",
                    }}>Past</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {pastEvents.map(ev => (
                      <EventCard
                        key={ev.id}
                        event={ev}
                        canEdit={canEditEvent(ev)}
                        canJoin={canJoinEvent(profile, ev, perms)}
                        onEdit={(e) => { setEditingEvent(e); setShowForm(true); }}
                        onArchive={archiveEvent}
                        onRSVP={toggleRSVP}
                        isAttending={isUserAttending(ev.attendees, profile?.id)}
                        profile={profile}
                        onManageAttendees={(e) => setManagingEvent(e)}
                        onShareToOrg={shareToOrg}
                        canShareToOrg={perms.isOrgAdmin}
                        reviewData={reviewDataMap[ev.id]}
                        hasReminder={!!userReminders[ev.id]}
                        reminderSchedule={userReminders[ev.id] || null}
                        onToggleReminder={toggleReminder}
                        isHighlighted={highlightedEventId === ev.id}
                        onManagePayouts={(e) => setPayoutEvent(e)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {visibleEvents.length === 0 && (
                <div style={{
                  textAlign: "center", padding: "clamp(40px, 8vw, 80px) 20px",
                }}>
                  <div style={{
                    fontFamily: "'Orbitron', sans-serif", fontSize: 32,
                    color: "rgba(255,255,255,0.06)", marginBottom: 16,
                  }}>◆</div>
                  <div style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 11,
                    color: "rgba(255,255,255,0.2)", letterSpacing: 2,
                    textTransform: "uppercase", marginBottom: 8,
                  }}>
                    {activeTab === "my-events"
                      ? "No events signed up for yet"
                      : "No events scheduled"
                    }
                  </div>
                  <div style={{
                    fontFamily: "'Rajdhani', sans-serif", fontSize: 14,
                    color: "rgba(232,234,246,0.3)",
                  }}>
                    {activeTab === "my-events"
                      ? "Sign up for events to see them here"
                      : canCreateForTab()
                        ? "Create one to get started"
                        : "Check back soon"
                    }
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Event form modal */}
      {showForm && (
        <EventFormModal
          event={editingEvent}
          teams={getCreatableTeams()}
          onSave={saveEvent}
          onClose={() => { setShowForm(false); setEditingEvent(null); }}
          canCreateOrgEvent={perms.isOrgAdmin}
          profile={profile}
        />
      )}

      {/* Attendee manager modal */}
      {managingEvent && (
        <AttendeeManagerModal
          event={managingEvent}
          onClose={() => setManagingEvent(null)}
          onSave={saveAttendees}
        />
      )}

      {/* Payout manager modal (contract events) */}
      {payoutEvent && (
        <PayoutManagerModal
          event={payoutEvent}
          profile={profile}
          onClose={() => setPayoutEvent(null)}
          onSave={savePayouts}
        />
      )}

      {/* Footer */}
      <footer style={{
        borderTop: "1px solid rgba(255,255,255,0.07)",
        padding: "40px clamp(16px, 4vw, 40px)",
        background: "rgba(0,0,0,0.3)",
        position: "relative", zIndex: 1,
      }}>
        <div style={{
          maxWidth: 1100, margin: "0 auto",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20,
        }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/images/logo.png"
              alt="Starkeeper"
              style={{ height: 28, width: "auto" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <span style={{
              fontFamily: "'Orbitron', sans-serif",
              fontWeight: 700, fontSize: 13, letterSpacing: 2.5, color: "rgba(232,234,246,0.5)",
              textTransform: "uppercase",
            }}>Starkeeper</span>
          </Link>
          <div style={{
            fontFamily: "'Space Mono', monospace", fontSize: 9,
            letterSpacing: 2, color: "rgba(255,255,255,0.25)", textTransform: "uppercase",
          }}>
            &copy; {new Date().getFullYear()} Starkeeper Industries
          </div>
        </div>
      </footer>
    </div>
  );
}
